/**
 * 사흘 — **단서가 붙는 물건들.**
 *
 * `scenario/사흘/05_단서표.md` §3(E01–E48)·§5(G01–G08)이 "정확한 장소"까지 못박아
 * 두었는데, 정작 그 자리에 놓을 물건이 라이브러리에 없었다. 노트북·복합기·금고처럼
 * 이미 있는 것은 그대로 쓰고, **없어서 조사가 성립하지 않던 것만** 여기 만든다.
 *
 * 이 파일의 물건들은 가구가 아니라 **증거**다. 그래서 판정 기준이 다르다 —
 * 「그 물건으로 보이는가」가 아니라 **「무엇이 잘못됐는지가 형태로 보이는가」**다.
 * 서류가방은 어깨끈 한쪽이 끊어져 바닥에 늘어져 있어야 하고, 비상키 캐비닛은
 * 고리 세 자리가 비어 있어야 하고, 머그는 깨진 채로 흩어져 있어야 한다.
 * 멀쩡한 물건을 놓고 설명문으로 때우면 조사가 텍스트 읽기로 퇴화한다.
 *
 * 좌표 규약은 `CONVENTIONS.md` 그대로 — 원점은 바닥 한가운데, 정면은 -Z.
 * 벽에 걸리는 것은 `hangs: true` + `sunk: true` 를 **둘 다** 단다. hangs 는
 * 「원점이 걸이 지점」임을 선언하고, sunk 는 그래서 몸통이 y=0 아래로 내려가는 것이
 * 의도임을 접지 게이트에 알린다. 하나만 달면 반드시 게이트에 걸린다.
 */

import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter, type Slot } from './kernel';
import { bloodPool } from './flesh';
import { paintGeometry, type Piece } from './pieces';

/* ------------------------------------------------------------------ *
 * HS13 — 시신
 * ------------------------------------------------------------------ */

/** 두 점을 잇는 원뿔대 하나. 팔·다리는 굵기가 변해야 관으로 안 보인다 */
function limb(a: THREE.Vector3, b: THREE.Vector3, r0: number, r1: number, seg = 10) {
  const dir = new THREE.Vector3().subVectors(b, a);
  const len = dir.length();
  const g = new THREE.CylinderGeometry(r1, r0, len, seg, 1, false);
  g.translate(0, len / 2, 0);
  g.applyQuaternion(
    new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize()),
  );
  g.translate(a.x, a.y, a.z);
  return g;
}

/** 타원체 덩어리. 사람 몸은 상자가 아니라 덩어리 몇 개가 겹친 것이다 */
function blob(x: number, y: number, z: number, rx: number, ry: number, rz: number) {
  const g = new THREE.SphereGeometry(1, 16, 12);
  g.scale(rx, ry, rz);
  g.translate(x, y, z);
  return g;
}

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

const BODY_PIECE: Piece = {
  id: 'victim_body',
  name: '시신',
  collection: 'sahol',
  category: '단서',
  note:
    'HS13. 붙박이장에서 발견된 한서진. **웅크려 모로 누운 자세**여야 한다 — 무릎이 가슴 쪽으로 ' +
    '당겨져 있고 등이 굽어 있어야 「좁은 데로 밀어 넣은 것」(HS11·HS12)이 형태로 읽힌다. 반듯이 ' +
    '누인 자세로 만들면 은닉이라는 사실 자체가 사라진다.\n' +
    '**목의 압박 자국 띠와 옷깃에 걸린 검은 실 몇 가닥이 이 조각의 본체다.** 이 둘이 없으면 ' +
    'HS13 은 존재하지 않고, 후반에 서류가방 어깨끈(PR10)과 맞춰 볼 것도 없어진다. 실은 목·어깨 ' +
    '위에 얹혀 있어야 하고, 자국은 목을 한 바퀴 감아야 한다.\n' +
    '**피 웅덩이를 깔지 마라.** HS12 가 「대규모 혈흔 없음」이고, 그게 사고사로 오인시키는 장치다. ' +
    '피를 깔면 첫 화면에서 살인이 확정돼 시나리오 1막이 통째로 무너진다.\n' +
    '난방이 꺼진 방에 사흘 있었다 — 살빛은 따뜻하면 안 된다.',
  dims: { w: 1.02, h: 0.33, d: 0.42 },
  clusters: 1,
  build(w) {
    // 살빛. **hide(#d6c39e) 를 쓰면 안 된다** — 첫 렌더에서 얼굴이 크림색으로 나왔다.
    // 노란 바탕에 무엇을 곱해도 노란기가 남는다(곱하기라 채널이 커지지 않는다).
    // paint(#cdc8be) 는 거의 무채색이라 차가운 회청을 곱하면 (140,142,150) 근처로 떨어진다 —
    // 난방 꺼진 방에 사흘 있은 살빛이다
    const skin = (g: THREE.BufferGeometry) => paintGeometry(g, '#aeb5c9');
    // 얼굴 이목구비·머리카락 그늘. 곱하기라 어둡게만 할 수 있으니 어두운 쪽에 쓴다
    const dark = (g: THREE.BufferGeometry) => paintGeometry(g, '#3a3f4a');
    // 셔츠는 **칠하지 않는다.** fabric(#4d5262) 자체가 이미 어두운 남회색인데
    // paintGeometry 는 곱하기라 한 번 더 칠하면 검은 덩어리가 된다 — 첫 렌더에서
    // 상체가 통째로 실루엣이 되어 목의 검은 실이 배경과 구분되지 않았다
    const trouser = (g: THREE.BufferGeometry) => paintGeometry(g, '#9aa0ac');

    w.part('상체');
    // 가슴·배·엉덩이 세 덩어리. 하나짜리 타원체는 즉시 「누에고치」가 된다
    const chest = blob(-0.09, 0.155, 0.0, 0.22, 0.155, 0.125);
    w.add(chest, 'fabric', 0.6);
    const belly = blob(0.2, 0.135, 0.012, 0.17, 0.135, 0.115);
    w.add(belly, 'fabric', 0.6);

    w.part('골반');
    const hip = blob(0.35, 0.128, 0.022, 0.13, 0.128, 0.115);
    trouser(hip);
    w.add(hip, 'fabric', 0.5);

    w.part('목');
    const neck = limb(V(-0.24, 0.13, 0.0), V(-0.335, 0.115, -0.008), 0.055, 0.05);
    skin(neck);
    w.add(neck, 'paint', 0.3);

    w.part('머리');
    // 모로 누웠으니 귀-귀가 세로(Y), 정수리-턱이 몸통 축(X), 얼굴-뒤통수가 Z 다.
    // 얼굴은 -Z 를 본다 (CONVENTIONS.md 정면 규약)
    const head = blob(-0.425, 0.105, -0.022, 0.115, 0.078, 0.095);
    skin(head);
    w.add(head, 'paint', 0.3);
    const jaw = blob(-0.368, 0.09, -0.042, 0.05, 0.046, 0.05);
    skin(jaw);
    w.add(jaw, 'paint', 0.2);
    // 코와 (위쪽) 귀. 없으면 머리가 **매끈한 달걀**이 되고, 달걀은 어느 쪽이
    // 얼굴인지 말해 주지 않는다. 모로 누웠으므로 귀는 위쪽 하나만 보인다
    const nose = blob(-0.436, 0.104, -0.108, 0.019, 0.015, 0.032);
    skin(nose);
    w.add(nose, 'paint', 0.1);
    const ear = blob(-0.417, 0.171, 0.002, 0.026, 0.016, 0.032);
    skin(ear);
    w.add(ear, 'paint', 0.1);

    w.part('이목구비');
    // 감은 눈 두 줄과 입. **이게 없으면 얼굴이 달걀로 읽힌다** — 첫 렌더가 그랬다.
    // 모로 누웠으므로 두 눈은 세로(Y)로 벌어지고, 정수리-턱 축은 X 다.
    // 얼굴 껍질 안쪽으로 3mm 밀어 넣어 파낸 홈처럼 얇게만 드러나게 한다
    for (const [ex, ey, ez, rx, ry, rz] of [
      [-0.442, 0.142, -0.1, 0.023, 0.006, 0.015],
      [-0.442, 0.07, -0.102, 0.023, 0.006, 0.015],
      [-0.383, 0.096, -0.09, 0.007, 0.019, 0.012],
    ] as const) {
      const f = blob(ex, ey, ez, rx, ry, rz);
      dark(f);
      w.add(f, 'paint', 0.05);
    }

    w.part('머리카락');
    // **구 하나로 만들지 마라.** 머리보다 2mm 큰 구를 뒤로 민 첫 판은 두 구가
    // 최대단면에서 만나 얼굴을 가로지르는 **완벽한 원**을 남겼다 — 사발을 엎어 쓴 꼴이었다.
    // 크기가 다른 덩어리 셋(정수리·뒤통수·옆머리)을 어긋나게 겹쳐 경계선을 부순다
    // **뒤로 밀지 마라.** 두 번째 판은 뒤통수(+Z) 로 16mm 밀었더니 정면에서 머리 뒤에
    // 완전히 가려 **대머리**로 나왔다. 얼굴이 드러나는 것은 밀어서가 아니라 머리 덩어리가
    // 머리카락 껍질보다 **앞(-Z)으로 15mm 튀어나와** 있기 때문이다 — 그 15mm 가 얼굴이다
    for (const [hx, hy, hz, rx, ry, rz] of [
      [-0.436, 0.115, 0.002, 0.124, 0.089, 0.104],
      [-0.352, 0.126, 0.05, 0.066, 0.06, 0.062],
      [-0.474, 0.078, 0.028, 0.076, 0.056, 0.076],
    ] as const) {
      w.add(blob(hx, hy, hz, rx, ry, rz), 'plastic', 0.4);
    }
    // 바닥에 퍼진 머리채. **가는 원뿔대로 뽑지 마라** — 두 번째 판은 철사 몇 가닥처럼 보였다.
    // 눌린 덩어리로 깔고 굵은 가닥 둘만 삐져나오게 한다
    w.add(blob(-0.472, 0.026, 0.052, 0.096, 0.026, 0.086), 'plastic', 0.3);
    for (const [a, c] of [
      [V(-0.5, 0.03, 0.08), V(-0.585, 0.012, 0.104)],
      [V(-0.49, 0.036, 0.02), V(-0.573, 0.011, -0.028)],
    ] as [THREE.Vector3, THREE.Vector3][]) {
      w.add(limb(a, c, 0.011, 0.005, 6), 'plastic', 0.1);
    }

    w.part('옷깃');
    const collar = new THREE.TorusGeometry(0.062, 0.018, 6, 14);
    collar.rotateY(Math.PI / 2);
    collar.translate(-0.235, 0.14, 0.0);
    w.add(collar, 'fabric', 0.3);

    w.part('압박흔');
    // **HS13 의 절반.** 목을 한 바퀴 감는 띠. 끈으로 조인 자국이라 폭이 좁고
    // 뒤쪽에서 겹쳐 두 줄이 된다 — 그 겹침이 「감아 당겼다」를 말한다
    for (const [dx, r, tube] of [
      [-0.29, 0.058, 0.0085],
      [-0.303, 0.056, 0.0062],
    ] as const) {
      const mark = new THREE.TorusGeometry(r, tube, 6, 16);
      mark.rotateY(Math.PI / 2);
      mark.rotateZ(0.13);
      mark.translate(dx, 0.121, -0.004);
      paintGeometry(mark, '#6b4f57');
      w.add(mark, 'paint', 0.2);
    }

    w.part('검은 섬유');
    // **HS13 의 나머지 절반.** 어깨끈에서 풀려 나온 실 몇 가닥이 목·옷깃 위에 얹혀 있다.
    // 굵기를 진짜 미세섬유(0.1mm)로 하면 화면에서 사라진다 — 올이 풀린 실 정도로 키운다
    // **셔츠 위에 놓지 마라.** 첫 판은 검은 실을 남회색 셔츠(#4d5262) 위에 얹어서
    // 검정 위 검정이 되어 아예 안 보였다. 밝아진 목살과 옷깃 위를 지나가게 건다.
    // 굵기도 진짜 미세섬유(0.1mm)면 화면에서 사라진다 — 올 풀린 실 정도로 키운다
    const fibers: [THREE.Vector3, THREE.Vector3][] = [
      [V(-0.318, 0.15, -0.042), V(-0.258, 0.178, -0.008)],
      [V(-0.3, 0.164, 0.032), V(-0.246, 0.181, 0.026)],
      [V(-0.284, 0.176, -0.028), V(-0.228, 0.204, -0.01)],
      [V(-0.262, 0.18, 0.012), V(-0.213, 0.212, 0.03)],
      [V(-0.246, 0.186, -0.034), V(-0.196, 0.208, -0.022)],
    ];
    for (const [a, b] of fibers) w.add(limb(a, b, 0.0024, 0.0018, 5), 'plastic', 0.05);

    w.part('아래팔');
    // 바닥에 깔린 오른팔
    const rUp = limb(V(-0.15, 0.075, -0.035), V(0.06, 0.062, -0.115), 0.045, 0.036);
    w.add(rUp, 'fabric', 0.4);
    const rLo = limb(V(0.06, 0.062, -0.115), V(0.2, 0.056, -0.05), 0.036, 0.028);
    w.add(rLo, 'fabric', 0.4);
    const rHand = blob(0.225, 0.052, -0.036, 0.042, 0.026, 0.033);
    skin(rHand);
    w.add(rHand, 'paint', 0.2);

    w.part('위팔');
    // 가슴 앞으로 끌어당겨진 왼팔
    const lUp = limb(V(-0.145, 0.255, -0.045), V(0.045, 0.192, -0.14), 0.045, 0.036);
    w.add(lUp, 'fabric', 0.4);
    const lLo = limb(V(0.045, 0.192, -0.14), V(0.155, 0.122, -0.158), 0.036, 0.028);
    w.add(lLo, 'fabric', 0.4);
    const lHand = blob(0.176, 0.108, -0.163, 0.04, 0.03, 0.032);
    skin(lHand);
    w.add(lHand, 'paint', 0.2);

    w.part('다리');
    // 무릎이 가슴 쪽(-X)·앞(-Z)으로 당겨진다. 이게 웅크린 자세의 전부다
    const legs: [number, number][] = [
      [0.088, -0.012],
      [0.212, 0.052],
    ];
    for (const [yb, zb] of legs) {
      const hipP = V(0.36, yb + 0.03, 0.02 + zb * 0.4);
      const knee = V(0.15, yb + 0.012, -0.148 + zb);
      const ankle = V(0.4, yb - 0.008, -0.196 + zb);
      const th = limb(hipP, knee, 0.075, 0.055);
      trouser(th);
      w.add(th, 'fabric', 0.5);
      const ca = limb(knee, ankle, 0.055, 0.036);
      trouser(ca);
      w.add(ca, 'fabric', 0.5);
      // 집 안이라 신발이 아니라 양말이다. fabric 은 곱하기로 밝게 못 하니 paint 를 쓴다
      const foot = blob(ankle.x + 0.035, ankle.y - 0.004, ankle.z - 0.012, 0.055, 0.032, 0.036);
      // 첫 렌더에서 **양말이 화면에서 가장 밝았다** — 시선이 발로 갔다. 회색 양말로 낮춘다
      paintGeometry(foot, '#8f96a3');
      w.add(foot, 'paint', 0.2);
    }
  },
};


/* ------------------------------------------------------------------ *
 * 공용 도구
 * ------------------------------------------------------------------ */

/**
 * 인쇄·필기된 면. **발광하지 않는다** — 화이트보드와 달력은 종이지 화면이 아니다.
 *
 * 글자를 진짜 글자로 그리지 않는다. 이 파이프라인의 캔버스는 시스템 폰트를 타는데
 * 서버에 한글 폰트가 없으면 통째로 두부(□)가 된다. 어차피 플레이어는 조사 UI 에서
 * 본문을 읽으므로, 3D 면에는 **획과 격자**만 있으면 「적힌 물건」으로 읽힌다.
 */
export function printedFace(
  draw: (g: CanvasRenderingContext2D, w: number, h: number) => void,
  w: number,
  h: number,
  bg: string,
) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = bg;
  g.fillRect(0, 0, w, h);
  draw(g, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({
    map: tex,
    roughness: 0.86,
    metalness: 0,
    side: THREE.DoubleSide,
    vertexColors: true,
  });
}

/** 켜진 화면. sahol.ts 의 것과 같은 물건이지만 그쪽이 모듈 지역이라 여기 다시 둔다 */
export function litFace(
  draw: (g: CanvasRenderingContext2D, w: number, h: number) => void,
  w: number,
  h: number,
) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0b0e12';
  g.fillRect(0, 0, w, h);
  draw(g, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({
    map: tex,
    emissiveMap: tex,
    emissive: new THREE.Color('#ffffff'),
    emissiveIntensity: 1.1,
    roughness: 0.22,
    metalness: 0,
    vertexColors: true,
  });
}

/**
 * 지오메트리의 UV 를 아틀라스 한 칸으로 옮긴다.
 *
 * 게시판 종이 열한 장이 **같은 무늬**면 복사-붙여넣기가 눈에 보인다. 한 장의 캔버스에
 * 서로 다른 문서 열두 개를 그려 두고 장마다 다른 칸을 보게 한다. `w.add(..., 0)` 은
 * 지오메트리가 가진 UV 를 그대로 쓰므로, 넘기기 전에 여기서 고쳐 두면 된다.
 */
export function atlasUV(geo: THREE.BufferGeometry, col: number, row: number, cols: number, rows: number) {
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, (col + uv.getX(i)) / cols, (rows - 1 - row + uv.getY(i)) / rows);
  }
  uv.needsUpdate = true;
}

/** 결정적이되 씨앗만 있으면 재현되는 난수. 조각마다 같은 자리에 같은 흠집이 나야 한다 */
export function rnd(i: number) {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * ExtrudeGeometry 의 UV 를 0..1 로 되돌린다.
 *
 * 기본 WorldUVGenerator 는 UV 를 **미터 단위 도형 좌표**로 뱉는다(-0.044 ~ 0.025 같은 값).
 * 그 상태로 w.add(geo, slot, 0) 을 하면 uvTile<=0 이라 그 UV 가 그대로 쓰이고,
 * 텍스처는 한 텍셀만 샘플되어 **순백의 판**이 된다 — 실제로 그렇게 나왔다.
 */
export function sheetUV(geo: THREE.BufferGeometry, minX: number, minY: number, w: number, h: number) {
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, (uv.getX(i) - minX) / w, (uv.getY(i) - minY) / h);
  }
  uv.needsUpdate = true;
}

/**
 * 깨진 도자기 조각 하나.
 *
 * 얇은 상자로 만들면 **트럼프 카드가 흩어진 것**처럼 보인다 — 실제로 그렇게 나왔다.
 * 컵 벽이었던 조각은 (1) 윤곽이 불규칙하고 (2) 원래 컵의 곡률이 남아 휘어 있다.
 * 불규칙 다각형을 뽑아 두께를 준 뒤 x² 에 비례해 들어 올려 그 둘을 한꺼번에 만든다.
 * 압출체이므로 휘어도 위상은 닫힌 채다.
 */
export function shard(seed: number, size: number, thick: number, curve: number) {
  const n = 5 + Math.floor(rnd(seed) * 3);
  const sh = new THREE.Shape();
  for (let i = 0; i < n; i++) {
    // 각도는 단조 증가시킨다 — 흔들림이 각 간격을 넘으면 다각형이 제 몸을 가로지른다
    const a = (i / n) * Math.PI * 2 + rnd(seed + i * 3) * 0.45;
    const r = size * (0.36 + rnd(seed + i * 7) * 0.64);
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r * 0.82;
    if (i === 0) sh.moveTo(x, y);
    else sh.lineTo(x, y);
  }
  const g = new THREE.ExtrudeGeometry(sh, { depth: thick, bevelEnabled: false });
  g.rotateX(-Math.PI / 2); // Shape 는 XY 평면에 눕혀 만든다. 세워 둔 것을 바닥에 눕힌다
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, pos.getY(i) + curve * pos.getX(i) * pos.getX(i));
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

/**
 * 늘어진 **띠**. 짧은 판을 이어 붙인다.
 *
 * `TubeGeometry(..., false)` 는 양 끝이 열려 solid-check 에 걸리고, 애초에 원통이라
 * 가죽끈으로 안 읽힌다. 판을 이어 붙이면 닫힌 몸체 여럿이고, 진행 방향으로 최소
 * 회전만 주므로 **매달린 구간은 넓은 면이 앞을 보고 바닥에 깔린 구간은 위를 본다** —
 * 실제 끈이 그렇게 눕는다.
 */
export function ribbon(w: MeshWriter, pts: THREE.Vector3[], width: number, thick: number, slot: Slot) {
  const FWD = new THREE.Vector3(0, 0, 1);
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const dir = b.clone().sub(a);
    const len = dir.length();
    if (len < 1e-4) continue;
    // 마디를 두께만큼 길게 만들어 이음매를 겹친다. 안 겹치면 joint-check 가 조각을 쪼갠다
    const g = bevelBox(width, thick, len + thick * 1.5, Math.min(0.0015, thick / 3));
    const q = new THREE.Quaternion().setFromUnitVectors(FWD, dir.clone().normalize());
    g.applyMatrix4(
      new THREE.Matrix4().compose(a.clone().lerp(b, 0.5), q, new THREE.Vector3(1, 1, 1)),
    );
    w.add(g, slot, 0.2);
  }
}

/** 두 점 사이로 처져 내려가는 점열. sag 는 두 점 이은 직선보다 가운데가 더 내려가는 양 */
export function droop(a: THREE.Vector3, b: THREE.Vector3, sag: number, n = 7): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const p = a.clone().lerp(b, t);
    p.y -= sag * Math.sin(Math.PI * t);
    out.push(p);
  }
  return out;
}

/** 자유롭게 꺾이는 경로를 부드럽게 이은 점열 */
export function spline(pts: [number, number, number][], n: number) {
  const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)));
  return curve.getPoints(n);
}

/**
 * 7세그먼트 숫자. 벽 온도조절기의 설정온도는 **읽혀야 한다** — 그게 E07 의 전부다.
 * 폰트를 쓰면 서버에서 두부가 되므로 막대 일곱 개로 직접 그린다.
 */
export const SEG: Record<string, number[]> = {
  '0': [1, 1, 1, 1, 1, 1, 0],
  '1': [0, 1, 1, 0, 0, 0, 0],
  '2': [1, 1, 0, 1, 1, 0, 1],
  '3': [1, 1, 1, 1, 0, 0, 1],
  '4': [0, 1, 1, 0, 0, 1, 1],
  '5': [1, 0, 1, 1, 0, 1, 1],
  '6': [1, 0, 1, 1, 1, 1, 1],
  '7': [1, 1, 1, 0, 0, 0, 0],
  '8': [1, 1, 1, 1, 1, 1, 1],
  '9': [1, 1, 1, 1, 0, 1, 1],
};

export function seg7(g: CanvasRenderingContext2D, ch: string, x: number, y: number, w: number, h: number, t: number) {
  const on = SEG[ch];
  if (!on) return;
  const bars: [number, number, number, number][] = [
    [x + t, y, w - t * 2, t], // a 위
    [x + w - t, y + t, t, h / 2 - t * 1.5], // b 오른위
    [x + w - t, y + h / 2 + t / 2, t, h / 2 - t * 1.5], // c 오른아래
    [x + t, y + h - t, w - t * 2, t], // d 아래
    [x, y + h / 2 + t / 2, t, h / 2 - t * 1.5], // e 왼아래
    [x, y + t, t, h / 2 - t * 1.5], // f 왼위
    [x + t, y + h / 2 - t / 2, w - t * 2, t], // g 가운데
  ];
  bars.forEach((b, i) => {
    g.fillStyle = on[i] ? '#eaf6ff' : 'rgba(90,120,140,0.14)';
    g.fillRect(b[0], b[1], b[2], b[3]);
  });
}

/** 손으로 그은 획 한 줄. 시작·끝이 흐려지고 두께가 흔들려야 마커 자국으로 보인다 */
export function stroke(g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, wdt: number, seed: number) {
  g.lineCap = 'round';
  g.lineWidth = wdt;
  g.beginPath();
  g.moveTo(x0, y0);
  const n = 4;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    g.lineTo(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t + (rnd(seed + i) - 0.5) * wdt * 1.6);
  }
  g.stroke();
}

/* ------------------------------------------------------------------ *
 * 물건
 * ------------------------------------------------------------------ */

export const SAHOL_EVIDENCE_PIECES: Piece[] = [
  /* ---------------- 벽에 걸리는 공개면 ---------------- */
  {
    id: 'whiteboard',
    mount: 'wall',
    name: '화이트보드',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E01. 회의실 정면. 첫 회의에서 적힌 다섯 항목이 지워지지 않고 남아 있다 — 한 줄만 빨강으로 동그라미가 쳐져 있는데 그게 이 사건의 시작이다. 판면 글씨는 획으로만 그린다(폰트를 태우면 서버에서 두부가 된다). 본문은 조사 UI 가 읽어 준다. */
    note: '벽에 거는 대형 화이트보드. 흰 판면을 얇은 알루미늄 테가 두르고 아래에 마커·지우개를 얹는 트레이가 붙는다. 판면에 손글씨 몇 줄과 빨간 동그라미 표시가 남아 있어 회의 자리의 보드로 읽힌다. dims 는 w·h(판 크기)·t(두께)·tray(트레이 깊이).',
    hangs: true,
    sunk: true,
    dims: { w: 1.8, h: 1.05, t: 0.028, tray: 0.055 },
    /* 트레이 위의 지우개를 집어 판을 지운다 (2026-09-16) */
    openable: [{ part: '지우개', slide: [0, 0, -0.05] }],
    build(w, d) {
      const fr = 0.028; // 알루미늄 테두리 폭
      const face = -d.t / 2 + 0.008; // 판면은 테두리보다 8mm 들어가 있다

      w.part('테두리');
      const rim: [number, number, number, number][] = [
        [0, -fr / 2, d.w, fr],
        [0, -d.h + fr / 2, d.w, fr],
        [-d.w / 2 + fr / 2, -d.h / 2, fr, d.h - fr * 2],
        [d.w / 2 - fr / 2, -d.h / 2, fr, d.h - fr * 2],
      ];
      for (const [x, y, bw, bh] of rim) {
        const g = bevelBox(bw, bh, d.t, 0.003);
        g.translate(x, y, 0);
        w.add(g, 'alu', 0.3);
      }

      w.part('판');
      const pw = d.w - fr * 2;
      const ph = d.h - fr * 2;
      const panel = bevelBox(pw, ph, d.t * 0.62, 0.002);
      panel.translate(0, -d.h / 2, d.t * 0.19);
      w.add(panel, 'paint', 0.6);

      w.part('필기면');
      w.thinSurface(() => {
        const p = new THREE.PlaneGeometry(pw - 0.004, ph - 0.004);
        p.rotateY(Math.PI); // PlaneGeometry 는 +Z 를 본다. 정면(-Z)에 놓으려면 뒤집는다
        p.translate(0, -d.h / 2, face);
        w.add(p, 'painted', 0);
      });

      w.part('펜 트레이');
      const tw = d.w * 0.44;
      const shelf = bevelBox(tw, 0.01, d.tray, 0.002);
      shelf.translate(-d.w * 0.16, -d.h + fr * 0.4, -d.t / 2 - d.tray / 2 + 0.006);
      w.add(shelf, 'alu', 0.3);
      const lip = bevelBox(tw, 0.018, 0.006, 0.002);
      lip.translate(-d.w * 0.16, -d.h + fr * 0.4 + 0.008, -d.t / 2 - d.tray + 0.009);
      w.add(lip, 'alu', 0.3);

      w.part('마커');
      // 트레이 판에 1mm 묻어 놓는다. 정확히 얹으면 부동소수 오차로 떨어진 덩이가 된다
      for (let i = 0; i < 3; i++) {
        const pen = lathe(
          [
            [0, 0],
            [0.0075, 0],
            [0.0075, 0.11],
            [0.006, 0.122],
            [0, 0.122],
          ],
          10,
        );
        pen.rotateZ(Math.PI / 2);
        pen.translate(-d.w * 0.16 - 0.055 + i * 0.024 - 0.055, -d.h + fr * 0.4 + 0.0085, -d.t / 2 - d.tray * 0.5 + 0.006);
        w.add(pen, 'plastic', 0.2);
      }

      w.part('지우개');
      w.movable('지우개', () => {
        const er = bevelBox(0.058, 0.026, 0.032, 0.004);
        er.translate(-d.w * 0.16 + 0.11, -d.h + fr * 0.4 + 0.014, -d.t / 2 - d.tray * 0.5 + 0.006);
        w.add(er, 'plastic', 0.2);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 오래 쓴 판은 지운 자국이 유령처럼 남는다. 이게 없으면 새 판때기로 보인다
          g.fillStyle = 'rgba(120,135,140,0.07)';
          for (let i = 0; i < 14; i++) {
            const x = rnd(i) * W;
            const y = rnd(i + 40) * H;
            g.fillRect(x, y, 60 + rnd(i + 80) * 190, 16 + rnd(i + 120) * 26);
          }
          // 머리줄 하나 — 그 아래로 항목 다섯
          g.strokeStyle = '#1d2733';
          stroke(g, W * 0.07, H * 0.15, W * 0.42, H * 0.15, 7, 3);
          for (let r = 0; r < 5; r++) {
            const y = H * (0.3 + r * 0.13);
            g.fillStyle = '#1d2733';
            g.fillRect(W * 0.07, y - 6, 12, 12); // 글머리표
            g.strokeStyle = r === 2 ? '#1d2733' : '#22303d';
            let x = W * 0.11;
            const words = 3 + Math.floor(rnd(r * 7) * 3);
            for (let k = 0; k < words; k++) {
              const len = 44 + rnd(r * 13 + k) * 96;
              stroke(g, x, y, x + len, y, 6, r * 31 + k);
              x += len + 22;
            }
          }
          // 세 번째 줄만 빨강 동그라미. 회의에서 결론이 난 항목이다
          g.strokeStyle = '#c8362f';
          g.lineWidth = 7;
          g.beginPath();
          g.ellipse(W * 0.34, H * 0.56, W * 0.29, H * 0.075, 0.02, 0, Math.PI * 2);
          g.stroke();
        },
        1024,
        600,
        '#f1f2ef',
      ),
    }),
  },

  {
    id: 'notice_board',
    mount: 'wall',
    name: '게시판',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E05(사업일정)·E04(수사자료 보드) 겸용. 코르크에 압정으로 꽂은 종이 열한 장. 한 장은 압정이 하나뿐이라 기울어 있고 한 장은 최근에 떼어 낸 자리가 압정 구멍만 남아 비어 있다 — **뗀 자리가 보이는 것**이 이 물건의 단서다. */
    note: '코르크 게시판. 나무 테 안의 코르크 면에 종이 여러 장을 압정으로 꽂아 둔 형태다. 한 장은 압정이 하나뿐이라 기울어 있고 한 자리는 종이를 떼어 내 압정 구멍만 남아 비어 있어, 쓰이고 있는 게시판으로 읽힌다. dims 는 w·h·t(판 두께).',
    hangs: true,
    sunk: true,
    dims: { w: 1.18, h: 0.86, t: 0.034 },
    /* 뗀 자리 압정에 한 귀퉁이로 매달린 종이를 떼어 낸다 (2026-09-16) */
    openable: [{ part: '기운 종이', slide: [0, -0.02, -0.05] }],
    build(w, d) {
      const fr = 0.036;

      w.part('테두리');
      const rim: [number, number, number, number][] = [
        [0, -fr / 2, d.w, fr],
        [0, -d.h + fr / 2, d.w, fr],
        [-d.w / 2 + fr / 2, -d.h / 2, fr, d.h - fr * 2],
        [d.w / 2 - fr / 2, -d.h / 2, fr, d.h - fr * 2],
      ];
      for (const [x, y, bw, bh] of rim) {
        const g = bevelBox(bw, bh, d.t, 0.003);
        g.translate(x, y, 0);
        w.add(g, 'oak', 0.25);
      }

      w.part('코르크');
      const cw = d.w - fr * 2;
      const ch = d.h - fr * 2;
      const corkT = d.t * 0.6;
      const corkZ = d.t * 0.2;
      const cork = bevelBox(cw, ch, corkT, 0.002);
      cork.translate(0, -d.h / 2, corkZ);
      w.add(cork, 'cardboard', 0.35);

      /**
       * 코르크 **앞면**(-Z 쪽). 예전에는 `-d.t/2 + 0.006 = -0.011` 로 손으로 적었는데,
       * 코르크는 z=+0.0068 에 두께 0.0204 로 놓여 실제 앞면은 -0.0034 다 — 종이가
       * 판에서 **8.2mm 앞에 떠 있었고** joint-check 가 7덩이로 잡았다(허용 1).
       * 코르크의 제 값에서 유도해서 다시 어긋나지 않게 한다.
       */
      const surf = corkZ - corkT / 2;

      w.part('종이');
      // A4 세로 여덟 · 가로 둘 · 넓은 일정표 하나. 겹치고 기울어야 게시판이 된다
      const sheets: [number, number, number, number, number][] = [
        [-0.40, -0.24, 0.19, 0.26, -0.03],
        [-0.19, -0.22, 0.19, 0.26, 0.02],
        [0.02, -0.25, 0.19, 0.26, -0.015],
        [0.24, -0.21, 0.26, 0.185, 0.025],
        [0.44, -0.31, 0.19, 0.26, -0.04],
        [-0.41, -0.55, 0.19, 0.26, 0.018],
        [-0.20, -0.57, 0.26, 0.185, -0.02],
        [0.05, -0.56, 0.19, 0.26, 0.03],
        [0.30, -0.58, 0.19, 0.26, -0.012],
        [-0.02, -0.62, 0.62, 0.14, 0.006], // 넓은 일정표 — 아래를 가로지른다
      ];
      w.thinSurface(() => {
        sheets.forEach(([x, y, sw, sh, rot], i) => {
          const p = new THREE.PlaneGeometry(sw, sh);
          atlasUV(p, i % 4, Math.floor(i / 4), 4, 3); // 장마다 다른 문서
          p.rotateY(Math.PI);
          p.rotateZ(rot);
          // 앞으로 갈수록 0.4mm 씩 띄운다. 같은 평면에 두면 겹친 데서 깜빡인다
          p.translate(x, y, surf - 0.0006 - i * 0.0004);
          w.add(p, 'paper', 0);
        });
      });

      w.part('압정');
      // 종이마다 위쪽에 하나. 세 번째 종이만 압정이 하나라 아래가 들려 있다
      const pins: [number, number][] = sheets.map(([x, y, , sh]) => [x, y + sh / 2 - 0.012]);
      pins.push([-0.06, -0.40]); // 뗀 자리 — 종이 없이 압정만 남았다
      pins.push([0.13, -0.40]);
      for (const [x, y] of pins) {
        const head = lathe(
          [
            [0, 0],
            [0.006, 0],
            [0.0062, 0.004],
            [0, 0.004],
          ],
          9,
        );
        head.rotateX(Math.PI / 2); // 축이 -Z 를 보게. 압정은 벽에서 튀어나온다
        head.translate(x, y, surf - 0.007);
        w.add(head, 'brass', 0.1);
      }

      /* **뗀 자리 압정(0.13,-0.40)에 걸린 종이** — 한 귀퉁이만 걸려 아래가 왼쪽으로
       * 쏠려 있다. 다른 종이들보다 앞에 떠 있어 덜렁거리는 장으로 읽히고,
       * E 키로 떼어 낼 수 있다 (2026-09-16) */
      w.movable('기운 종이', () => {
        w.thinSurface(() => {
          const p = new THREE.PlaneGeometry(0.19, 0.26);
          atlasUV(p, 3, 2, 4, 3);
          p.rotateY(Math.PI);
          p.rotateZ(0.42);
          p.translate(0.18, -0.52, surf - 0.006);
          w.add(p, 'paper', 0);
        });
      });
    },
    materials: () => ({
      paper: printedFace(
        (g, W, H) => {
          // 4x3 아틀라스. 칸마다 다른 서류 — 보고서·표·일정·도면 순으로 섞는다
          const cw = W / 4;
          const ch = H / 3;
          for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 4; c++) {
              const ox = c * cw;
              const oy = r * ch;
              const k = r * 4 + c;
              g.save();
              g.beginPath();
              g.rect(ox, oy, cw, ch);
              g.clip();
              g.fillStyle = k % 5 === 3 ? '#eceadf' : '#f6f5f0'; // 몇 장은 누렇게 바랬다
              g.fillRect(ox, oy, cw, ch);
              const m = cw * 0.12; // 여백
              // 머리글
              g.fillStyle = '#2b3540';
              g.fillRect(ox + m, oy + ch * 0.09, cw * (0.3 + rnd(k) * 0.34), ch * 0.022);
              g.fillStyle = '#7d868f';
              g.fillRect(ox + m, oy + ch * 0.14, cw * 0.22, ch * 0.012);
              if (k % 4 === 1) {
                // 표 — 시설·회계철에 흔한 꼴
                const rows = 6;
                g.strokeStyle = '#aeb4b8';
                g.lineWidth = 1;
                for (let t = 0; t <= rows; t++) {
                  const y = oy + ch * (0.24 + t * 0.09);
                  g.beginPath();
                  g.moveTo(ox + m, y);
                  g.lineTo(ox + cw - m, y);
                  g.stroke();
                }
                for (let t = 0; t <= 3; t++) {
                  const x = ox + m + t * ((cw - m * 2) / 3);
                  g.beginPath();
                  g.moveTo(x, oy + ch * 0.24);
                  g.lineTo(x, oy + ch * (0.24 + rows * 0.09));
                  g.stroke();
                }
                g.fillStyle = '#69727a';
                for (let t = 0; t < rows; t++) {
                  for (let u = 0; u < 3; u++) {
                    g.fillRect(
                      ox + m + u * ((cw - m * 2) / 3) + 4,
                      oy + ch * (0.28 + t * 0.09),
                      (cw - m * 2) / 3 * (0.3 + rnd(k * 30 + t * 3 + u) * 0.5),
                      ch * 0.011,
                    );
                  }
                }
              } else if (k % 4 === 2) {
                // 막대 그래프 — 일정·실적 게시물
                const bx = ox + m;
                const bw = (cw - m * 2) / 7;
                for (let t = 0; t < 7; t++) {
                  const hh = ch * (0.08 + rnd(k * 11 + t) * 0.34);
                  g.fillStyle = t === 4 ? '#b8503f' : '#7a93ab';
                  g.fillRect(bx + t * bw + 2, oy + ch * 0.76 - hh, bw - 5, hh);
                }
                g.strokeStyle = '#9aa1a6';
                g.beginPath();
                g.moveTo(bx, oy + ch * 0.76);
                g.lineTo(ox + cw - m, oy + ch * 0.76);
                g.stroke();
                g.fillStyle = '#69727a';
                for (let t = 0; t < 3; t++) {
                  g.fillRect(ox + m, oy + ch * (0.82 + t * 0.045), cw * (0.3 + rnd(k + t) * 0.4), ch * 0.011);
                }
              } else {
                // 본문 — 문단 셋
                let y = oy + ch * 0.24;
                for (let para = 0; para < 3; para++) {
                  const lines = 3 + Math.floor(rnd(k * 7 + para) * 4);
                  for (let t = 0; t < lines; t++) {
                    const last = t === lines - 1;
                    g.fillStyle = '#69727a';
                    g.fillRect(
                      ox + m,
                      y,
                      (cw - m * 2) * (last ? 0.3 + rnd(k * 13 + t) * 0.4 : 0.94),
                      ch * 0.012,
                    );
                    y += ch * 0.036;
                  }
                  y += ch * 0.03;
                }
              }
              g.restore();
            }
          }
        },
        1024,
        768,
        '#f6f5f0',
      ),
    }),
  },

  {
    id: 'wall_calendar',
    mount: 'wall',
    name: '벽걸이 달력',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E06. 회의실 공용 캘린더. 스프링 제본이라 아랫장이 벽에서 떠 배가 나온다. 날짜 칸 몇에 표시가 있고 한 칸은 빨강으로 동그라미 — 그 날짜가 사건 당일이다. */
    note: '벽걸이 달력. 위쪽 스프링 제본에 매달린 두 장짜리로, 아랫장이 벽에서 떠 배가 나온 형태가 특징이다. 날짜 칸 몇에 손 표시가 있고 한 칸에 빨간 동그라미가 쳐져 있다. dims 는 w·h·t(종이 두께)·tilt(아랫장이 뜨는 정도).',
    hangs: true,
    sunk: true,
    dims: { w: 0.4, h: 0.56, t: 0.005, tilt: 0.07 },
    /* 윗장이 스프링(위쪽 가장자리)을 축으로 넘어간다 — 한 달을 넘기는 동작.
     * 경첩 좌표는 스프링 줄의 조각 좌표: at(0,-0.02,0) 에 tilt 0.07 이 적용된 위치 */
    openable: [{ part: '달력 윗장', swing: 3.0, axis: 'x', pivot: [0, -0.026, 0.002] }],
    build(w, d) {
      w.part('걸이');
      // 벽못에 걸리는 고리. 원점이 여기다
      const hook = new THREE.TorusGeometry(0.009, 0.0018, 6, 14);
      hook.rotateY(Math.PI / 2);
      hook.translate(0, -0.009, 0.004);
      w.add(hook, 'steel', 0.05);

      // 종이는 아래로 갈수록 벽에서 떨어진다. 완전히 붙어 있으면 인쇄물 판때기다
      w.at(
        0,
        -0.02,
        0,
        () => {
          w.part('뒤판');
          const back = bevelBox(d.w, d.h, d.t, 0.002);
          back.translate(0, -d.h / 2, d.t / 2);
          w.add(back, 'cardboard', 0.3);

          w.part('달력면');
          w.thinSurface(() => {
            const p = new THREE.PlaneGeometry(d.w - 0.006, d.h - 0.006);
            p.rotateY(Math.PI);
            p.translate(0, -d.h / 2, -0.0006);
            w.add(p, 'painted', 0);
          });

          /* 넘기는 윗장 — 달력면과 **같은 인쇄 텍스처**를 입힌 얇은 면을 바로 앞에 얹는다.
           * 닫혀 있으면 아래 장과 그림이 같아 그대로이고, 스프링을 축으로 위로 넘기면
           * 아래 깔린 같은 장이 다음 달처럼 보인다 */
          w.movable('달력 윗장', () => {
            w.thinSurface(() => {
              const leaf = new THREE.PlaneGeometry(d.w - 0.006, d.h - 0.006);
              leaf.rotateY(Math.PI);
              leaf.translate(0, -d.h / 2, -0.0016);
              w.add(leaf, 'painted', 0);
            });
          });

          w.part('스프링');
          // 이중 링 제본. 이게 없으면 액자가 된다
          for (let i = 0; i < 11; i++) {
            const r = new THREE.TorusGeometry(0.011, 0.0012, 5, 12);
            r.rotateY(Math.PI / 2);
            r.translate(-d.w / 2 + 0.02 + i * ((d.w - 0.04) / 10), -0.006, d.t / 2);
            w.add(r, 'steel', 0.05);
          }
        },
        0,
        d.tilt,
      );
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 위 절반 — 눈 덮인 능선 사진. 이 마을의 달력이다
          const sky = g.createLinearGradient(0, 0, 0, H * 0.46);
          sky.addColorStop(0, '#48607a');
          sky.addColorStop(1, '#9db3c4');
          g.fillStyle = sky;
          g.fillRect(0, 0, W, H * 0.46);
          for (const [base, tone] of [
            [0.40, '#7d92a6'],
            [0.44, '#c9d6e0'],
          ] as const) {
            g.fillStyle = tone;
            g.beginPath();
            g.moveTo(0, H * base);
            for (let x = 0; x <= W; x += W / 12) {
              g.lineTo(x, H * base - (0.05 + rnd(x + base * 100) * 0.09) * H);
            }
            g.lineTo(W, H * 0.46);
            g.lineTo(0, H * 0.46);
            g.fill();
          }
          // 아래 절반 — 날짜 격자 7×5
          g.fillStyle = '#fbfbf8';
          g.fillRect(0, H * 0.46, W, H * 0.54);
          const gx = W * 0.06;
          const gy = H * 0.56;
          const cw = (W * 0.88) / 7;
          const chh = (H * 0.4) / 5;
          g.strokeStyle = '#c3c7c9';
          g.lineWidth = 2;
          for (let c = 0; c <= 7; c++) {
            g.beginPath();
            g.moveTo(gx + c * cw, gy);
            g.lineTo(gx + c * cw, gy + chh * 5);
            g.stroke();
          }
          for (let r = 0; r <= 5; r++) {
            g.beginPath();
            g.moveTo(gx, gy + r * chh);
            g.lineTo(gx + cw * 7, gy + r * chh);
            g.stroke();
          }
          // 날짜 숫자 자리 — 칸마다 작은 획 하나
          g.fillStyle = '#8b9095';
          for (let r = 0; r < 5; r++) {
            for (let c = 0; c < 7; c++) {
              g.fillRect(gx + c * cw + 6, gy + r * chh + 7, 13, 3);
            }
          }
          // 일정이 적힌 칸 넷
          g.fillStyle = '#5b7fa8';
          for (const [r, c] of [
            [1, 2],
            [2, 5],
            [3, 1],
            [4, 3],
          ] as const) {
            g.fillRect(gx + c * cw + 6, gy + r * chh + chh * 0.55, cw - 14, 5);
          }
          // 사건 당일. 빨강 동그라미
          g.strokeStyle = '#c8362f';
          g.lineWidth = 4;
          g.beginPath();
          g.ellipse(gx + 4 * cw + cw / 2, gy + 3 * chh + chh / 2, cw * 0.42, chh * 0.38, 0, 0, Math.PI * 2);
          g.stroke();
          // 요일 머리줄
          g.fillStyle = '#c8362f';
          g.fillRect(gx, gy - 14, cw * 0.5, 4);
          g.fillStyle = '#6b7075';
          for (let c = 1; c < 7; c++) g.fillRect(gx + c * cw, gy - 14, cw * 0.5, 4);
        },
        512,
        720,
        '#fbfbf8',
      ),
    }),
  },

  {
    id: 'key_cabinet',
    mount: 'wall',
    name: '비상키 캐비닛',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E08. 현관 벽. 문이 열려 있고 고리 열둘 중 **셋이 비어 있다** — 비어 있는 자리가 이 물건의 전부다. 문 안쪽에 대여 규칙표가 붙어 있고 잠금 실린더는 멀쩡하다(부순 게 아니라 열고 가져갔다). */
    note: '벽걸이 키 캐비닛. 얇은 강판 상자에 여닫이문이 달렸고, 안에는 열쇠 고리 열두 개가 두 줄로 박혀 있다. 문이 열린 채 고리 몇 개가 비어 있으며 문 안쪽에 규칙표가 붙어 있고 잠금 실린더는 온전하다. dims 는 w·h·d 와 door(문 열림 각, 라디안).',
    hangs: true,
    sunk: true,
    clusters: 2,
    dims: { w: 0.34, h: 0.42, d: 0.085, door: 1.05 },
    /* 문은 왼쪽(-x) 경첩에서 1.05 rad 열린 채 구워 있다 — E 를 누르면 닫힌다 */
    openable: [{ part: '문', swing: -1.05, axis: 'y', pivot: [-0.17, -0.21, 0], inside: { w: 0.3, h: 0.36, d: 0.06, at: [0, -0.21, 0] } }],
    build(w, d) {
      const t = 0.006;
      const body = -d.h; // 몸통 밑면

      w.part('몸통');
      // 앞면은 문이 대신한다. 나머지 다섯 면을 판으로 짠다
      for (const [bw, bh, bd, x, y, z] of [
        [d.w, t, d.d, 0, body + t / 2, 0], // 바닥
        [d.w, t, d.d, 0, -t / 2, 0], // 천장
        [t, d.h - t * 2, d.d, -d.w / 2 + t / 2, body + d.h / 2, 0], // 왼쪽
        [t, d.h - t * 2, d.d, d.w / 2 - t / 2, body + d.h / 2, 0], // 오른쪽
        [d.w - t * 2, d.h - t * 2, t, 0, body + d.h / 2, d.d / 2 - t / 2], // 뒤판
      ] as const) {
        const g = bevelBox(bw, bh, bd, 0.0015);
        g.translate(x, y, z);
        w.add(g, 'steel', 0.25);
      }

      w.part('걸이못');
      // 3열 4행. 뒤판에서 앞으로 튀어나온다
      const pegX = (c: number) => -d.w / 2 + 0.055 + c * ((d.w - 0.11) / 2);
      const pegY = (r: number) => -0.055 - r * ((d.h - 0.12) / 3);
      /* 비어 있는 자리 — dims.hook(0부터 세는 상단 행 번호)이 오면 그 하나만 비고,
       * 없으면 예전처럼 세 자리가 빈다 (사흘 v8 1-e: 「3번 비어 있고 2번에 걸림」) */
      const taken = d.hook !== undefined ? new Set([`${d.hook},0`]) : new Set(['0,1', '1,0', '2,2']);
      for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 3; c++) {
          const peg = lathe(
            [
              [0, 0],
              [0.0022, 0],
              [0.0022, 0.016],
              [0.004, 0.017],
              [0.004, 0.019],
              [0, 0.019],
            ],
            8,
          );
          peg.rotateX(Math.PI / 2); // 축을 -Z 로: 못이 앞으로 나온다
          peg.translate(pegX(c), pegY(r), d.d / 2 - t - 0.019);
          w.add(peg, 'steel', 0.05);
        }
      }

      w.part('열쇠');
      for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 3; c++) {
          if (taken.has(`${c},${r}`)) continue;
          const x = pegX(c);
          const y = pegY(r);
          const z = d.d / 2 - t - 0.012;
          // 고리 — 못에 걸린다
          const ring = new THREE.TorusGeometry(0.0075, 0.0011, 5, 14);
          ring.translate(x, y - 0.0055, z);
          w.add(ring, 'steel', 0.05);
          // 이름표 — 고리 아래로 늘어진다. 살짝씩 다르게 돌아가 있어야 산 물건이다
          const tag = bevelBox(0.019, 0.028, 0.0022, 0.0008);
          tag.rotateZ((rnd(r * 3 + c) - 0.5) * 0.35);
          tag.translate(x, y - 0.03, z);
          w.add(tag, 'plastic', 0.06);
          // 열쇠 몸 — 이름표 뒤에 반쯤 가린다
          const key = bevelBox(0.006, 0.03, 0.0016, 0.0005);
          key.translate(x + 0.011, y - 0.026, z + 0.002);
          w.add(key, 'brass', 0.04);
        }
      }

      // 문 — 왼쪽 경첩에서 앞(-Z)으로 열려 있다
      w.movable('문', () => w.at(
        -d.w / 2,
        0,
        0,
        () => {
          w.part('문');
          const leaf = bevelBox(d.w, d.h, 0.008, 0.002);
          leaf.translate(d.w / 2, body + d.h / 2, -d.d / 2 - 0.004);
          w.add(leaf, 'steel', 0.25);

          w.part('규칙표');
          w.thinSurface(() => {
            const p = new THREE.PlaneGeometry(d.w * 0.7, d.h * 0.6);
            // 문 **안쪽**에 붙었으므로 열린 상태에서는 +Z 쪽 면을 본다. 뒤집지 않는다
            p.translate(d.w / 2, body + d.h * 0.58, -d.d / 2 + 0.0004);
            w.add(p, 'paper', 0);
          });

          w.part('손잡이');
          const knob = lathe(
            [
              [0, 0],
              [0.006, 0],
              [0.006, 0.018],
              [0.011, 0.02],
              [0.011, 0.026],
              [0, 0.026],
            ],
            10,
          );
          knob.rotateX(-Math.PI / 2); // 문 바깥면(+Z 쪽, 열려 있으니 뒤를 본다)
          knob.translate(d.w - 0.035, body + d.h * 0.5, -d.d / 2 + 0.004);
          w.add(knob, 'steel', 0.05);
        },
        d.door,
      ));
    },
  },

  {
    id: 'thermostat',
    mount: 'wall',
    name: '온도조절기',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E07. 침실 벽. 설정온도가 12도로 내려가 있다 — 숫자가 읽혀야 하므로 7세그먼트 막대로 직접 그린다. 난방을 누가 언제 내렸는지가 사망추정시각을 흔든다. */
    note: '벽걸이 디지털 온도조절기. 작은 흰 플라스틱 판에 7세그먼트 숫자 표시창과 조작 버튼이 붙은 형태다. 숫자는 폰트가 아니라 막대로 직접 그려 어느 각도에서도 읽힌다. dims 는 w·h·t.',
    hangs: true,
    sunk: true,
    dims: { w: 0.105, h: 0.072, t: 0.023 },
    /* 아래 모서리를 축으로 앞으로 젖혀 연다 — 고정편·배선을 본다 (2026-09-17) */
    openable: [{ part: '몸통', swing: -1.0, axis: 'x', pivot: [0, -0.072, 0] }],
    build(w, d) {
      w.movable('몸통', () => {
      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.t, 0.004);
      body.translate(0, -d.h / 2, 0);
      w.add(body, 'plastic', 0.12);

      w.part('계기판');
      // 앞면에서 1mm 파인 자리에 화면이 앉는다
      const well = bevelBox(d.w * 0.66, d.h * 0.5, 0.004, 0.001);
      well.translate(-d.w * 0.08, -d.h * 0.44, -d.t / 2 - 0.001);
      w.add(well, 'plastic', 0.1);
      w.thinSurface(() => {
        const p = new THREE.PlaneGeometry(d.w * 0.62, d.h * 0.46);
        p.rotateY(Math.PI);
        p.translate(-d.w * 0.08, -d.h * 0.44, -d.t / 2 - 0.0032);
        w.add(p, 'screen', 0);
      });

      w.part('버튼');
      for (let i = 0; i < 3; i++) {
        const b = lathe(
          [
            [0, 0],
            [0.006, 0],
            [0.006, 0.0025],
            [0, 0.0025],
          ],
          10,
        );
        b.rotateX(Math.PI / 2);
        b.translate(d.w * 0.36, -d.h * 0.22 - i * 0.019, -d.t / 2 - 0.0025);
        w.add(b, 'plastic', 0.06);
      }
      });
    },
    materials: () => ({
      screen: litFace((g, W, H) => {
        g.fillStyle = '#0a1a22';
        g.fillRect(0, 0, W, H);
        // 설정온도 12
        seg7(g, '1', W * 0.14, H * 0.16, W * 0.24, H * 0.62, W * 0.045);
        seg7(g, '2', W * 0.46, H * 0.16, W * 0.24, H * 0.62, W * 0.045);
        // 도 표시 — 작은 링
        g.strokeStyle = '#eaf6ff';
        g.lineWidth = W * 0.026;
        g.beginPath();
        g.arc(W * 0.82, H * 0.28, W * 0.05, 0, Math.PI * 2);
        g.stroke();
        // 난방 꺼짐 표시등 하나만 살아 있다
        g.fillStyle = '#3f6070';
        g.fillRect(W * 0.12, H * 0.86, W * 0.3, H * 0.06);
      }, 256, 160),
    }),
  },

  /* ---------------- 기기 ---------------- */
  {
    id: 'smart_speaker',
    mount: 'surface',
    nofront: true,
    name: '스마트 스피커',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E10. 침실 머리맡. 링 표시등이 켜져 있다 — 이게 켜져 있다는 건 그날 밤 누군가 말을 걸었다는 뜻이다. 천 그릴 몸통 · 플라스틱 캡 · 뒤로 늘어진 전원선. */
    note: '원통형 스마트 스피커. 천 그릴로 감싼 몸통 위에 플라스틱 캡이 얹히고, 캡 둘레의 링 표시등이 켜져 있다. 뒤로 늘어진 전원선 끝은 바닥 밑에 묻힌다. dims 는 r(반지름)·h(높이).',
    sunk: true, // 전원선 끝을 바닥 밑에 묻는다. 허공에서 끊기면 관 구멍이 보인다
    dims: { r: 0.05, h: 0.155 },
    /* 스피커를 통째로 들어 올린다 — 전원선이 딸려 온다 (2026-09-16) */
    openable: [{ part: '스피커', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.movable('스피커', () => {
      w.part('받침');
      const foot = lathe(
        [
          [0, 0],
          [d.r * 0.9, 0],
          [d.r * 0.92, 0.007],
          [0, 0.007],
        ],
        24,
      );
      w.add(foot, 'rubber', 0.1);

      w.part('그릴');
      // 천으로 감싼 통. 위로 갈수록 아주 조금 좁아진다 — 완전한 원기둥은 CG 티가 난다
      const grill = lathe(
        [
          [0, 0.007],
          [d.r * 0.94, 0.007],
          [d.r, 0.02],
          [d.r * 0.985, d.h - 0.03],
          [0, d.h - 0.03],
        ],
        26,
      );
      w.add(grill, 'fabric', 0.09);

      w.part('캡');
      // 그릴보다 가늘게 시작한다 — 어깨에 턱이 지고 그 턱에 표시등이 앉는다
      const cap = lathe(
        [
          [0, d.h - 0.031],
          [d.r * 0.93, d.h - 0.031],
          [d.r * 0.93, d.h - 0.014],
          [d.r * 0.86, d.h - 0.004],
          [d.r * 0.7, d.h],
          [0, d.h],
        ],
        26,
      );
      w.add(cap, 'plastic', 0.12);

      w.part('표시등');
      // 그릴 윗면과 캡 사이의 턱에 끼워 **밖으로 2mm 나오게** 한다.
      // 캡 반지름 안쪽에 두면 완전히 파묻혀 어두운 방에서 아무것도 안 보인다
      const ring = new THREE.TorusGeometry(d.r * 0.97, 0.0034, 6, 30);
      ring.rotateX(Math.PI / 2);
      ring.translate(0, d.h - 0.0265, 0);
      w.add(ring, 'screen', 0);

      w.part('전원선');
      const line = spline(
        [
          [0, 0.012, d.r * 0.8],
          [0.03, 0.006, d.r + 0.06],
          [0.11, 0.004, d.r + 0.11],
          [0.2, -0.01, d.r + 0.14],
        ],
        10,
      );
      ribbon(w, line, 0.005, 0.005, 'rubber');
      });
    },
    materials: () => ({
      screen: litFace((g, W, H) => {
        const grd = g.createLinearGradient(0, 0, W, 0);
        grd.addColorStop(0, '#2f6fe0');
        grd.addColorStop(0.5, '#43c7d8');
        grd.addColorStop(1, '#2f6fe0');
        g.fillStyle = grd;
        g.fillRect(0, 0, W, H);
      }, 64, 8),
    }),
  },

  {
    id: 'tablet_dock',
    mount: 'surface',
    name: '충전도크의 공용 태블릿',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E17 · 기믹 G04(잠금 0947) · G07(8초 음원 대조). 재무실 도크에 세워져 있고 화면에 음원 파형이 떠 있다 — 이 파형이 사진실·로스터리의 것과 같은 8초다. 도크에 꽂힌 채라 배터리는 항상 만충이고, 그래서 "꺼져 있었다"는 진술이 성립하지 않는다. */
    note: '충전 도크에 세워 둔 공용 태블릿. 낮은 도크 받침에 화면이 뒤로 기운 채 꽂혀 있고 화면에는 음원 파형 UI 가 떠 있다. 도크 옆으로 충전 케이블이 빠져나간다. dims 는 w·h·t(태블릿)·dockD·dockH(도크)·tilt(기울기).',
    sunk: true,
    dims: { w: 0.245, h: 0.168, t: 0.0075, dockD: 0.085, dockH: 0.052, tilt: 0.3 },
    /* 도크 홈에서 태블릿을 위로 뽑아 낸다 */
    openable: [{ part: '태블릿', slide: [0, 0.14, 0] }],
    build(w, d) {
      w.part('도크 바닥');
      const base = bevelBox(d.w * 0.72, 0.012, d.dockD, 0.003);
      base.translate(0, 0.006, 0);
      w.add(base, 'plastic', 0.2);

      w.part('도크 등받이');
      // 태블릿과 같은 각도로 눕는다. 각이 다르면 태블릿이 허공에 기댄 것으로 보인다
      const spine = bevelBox(d.w * 0.72, d.dockH, 0.012, 0.003);
      spine.applyMatrix4(new THREE.Matrix4().makeTranslation(0, d.dockH / 2, 0));
      spine.applyMatrix4(new THREE.Matrix4().makeRotationX(d.tilt));
      spine.translate(0, 0.01, d.dockD / 2 - 0.014);
      w.add(spine, 'plastic', 0.2);

      // 태블릿 — 도크 홈에 앉아 뒤로 기운다
      w.movable('태블릿', () => w.at(
        0,
        0.014,
        -0.004,
        () => {
          w.part('태블릿');
          const slab = bevelBox(d.w, d.h, d.t, 0.0025);
          slab.translate(0, d.h / 2, 0);
          w.add(slab, 'alu', 0.3);

          w.part('화면');
          w.thinSurface(() => {
            const p = new THREE.PlaneGeometry(d.w - 0.016, d.h - 0.016);
            p.rotateY(Math.PI);
            p.translate(0, d.h / 2, -d.t / 2 - 0.0006);
            w.add(p, 'screen', 0);
          });
        },
        0,
        d.tilt,
      ));

      w.part('충전선');
      const line = spline(
        [
          [0.02, 0.006, d.dockD / 2 - 0.01],
          [0.05, 0.004, d.dockD / 2 + 0.05],
          [0.12, 0.003, d.dockD / 2 + 0.09],
          [0.2, -0.01, d.dockD / 2 + 0.1],
        ],
        10,
      );
      ribbon(w, line, 0.004, 0.004, 'rubber');
    },
    materials: () => ({
      screen: litFace((g, W, H) => {
        g.fillStyle = '#10161d';
        g.fillRect(0, 0, W, H);
        // 상단 바
        g.fillStyle = '#1b2530';
        g.fillRect(0, 0, W, H * 0.11);
        g.fillStyle = '#5d7488';
        g.fillRect(W * 0.04, H * 0.04, W * 0.18, H * 0.035);
        // 파형 — 가운데 8초. 이 물건의 정체다
        const mid = H * 0.52;
        for (let i = 0; i < 96; i++) {
          const x = W * 0.06 + i * (W * 0.88 / 96);
          const env = Math.sin((i / 96) * Math.PI) ** 0.6;
          const a = (0.1 + rnd(i * 5) * 0.9) * env * H * 0.3;
          g.fillStyle = i < 62 ? '#4fc3d9' : '#31596b';
          g.fillRect(x, mid - a, W * 0.006, a * 2);
        }
        // 재생 위치
        g.fillStyle = '#f0f4f6';
        g.fillRect(W * 0.06 + 62 * (W * 0.88 / 96), H * 0.2, 2, H * 0.64);
        // 하단 진행바
        g.fillStyle = '#25313d';
        g.fillRect(W * 0.06, H * 0.88, W * 0.88, H * 0.02);
        g.fillStyle = '#4fc3d9';
        g.fillRect(W * 0.06, H * 0.88, W * 0.57, H * 0.02);
      }, 512, 352),
    }),
  },

  /* ---------------- 섬유 대조 3종 ---------------- */
  {
    id: 'seoryu_gabang',
    name: '서류가방',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E18. 배정호 좌석 옆. **어깨끈 한쪽 연결부가 뜯어져** 끈이 바닥에 늘어져 있고 반대쪽 D링은 비어 있다. 뜯긴 자리의 올이 풀려 있어 현장 섬유와 대조된다 — 멀쩡한 가방을 놓고 설명문으로 때우면 이 단서는 죽는다. */
    note: '가죽 서류가방. 각진 몸통에 손잡이와 D링 두 개가 달린 형태다. 어깨끈 한쪽 연결부가 뜯어져 끈이 바닥에 늘어져 있고 반대쪽 D링은 비어 있으며, 뜯긴 자리의 올이 풀려 있어 망가진 가방으로 읽힌다. dims 는 w·h·d 와 ring(D링 반지름).',
    dims: { w: 0.4, h: 0.285, d: 0.105, ring: 0.017 },
    /* 덮개를 위로 젖혀 안을 본다 — 서류가방은 열어 보는 것이다 (2026-09-16) */
    openable: [
      {
        part: '덮개',
        swing: 2.0,
        axis: 'x',
        pivot: [0, 0.279, -0.0565],
        inside: { w: 0.36, h: 0.22, d: 0.09, at: [0, 0.14, 0] },
      },
    ],
    build(w, d) {
      w.part('몸통');
      // 가죽 가방은 모서리가 둥글다. 베벨을 크게 준다
      const body = bevelBox(d.w, d.h, d.d, 0.016, 2);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'leather', 0.4);

      w.part('덮개');
      // 앞면(-Z)을 위에서 2/3 덮는다. 덮개가 없으면 그냥 상자다
      w.movable('덮개', () => {
        const flap = bevelBox(d.w * 1.01, d.h * 0.6, 0.012, 0.006);
        flap.translate(0, d.h * 0.68, -d.d / 2 - 0.004);
        w.add(flap, 'leather', 0.4);
        const lipEdge = bevelBox(d.w * 1.01, 0.014, d.d * 0.25, 0.005);
        lipEdge.translate(0, d.h - 0.006, -d.d / 2 + d.d * 0.11);
        w.add(lipEdge, 'leather', 0.4);
      });

      w.part('버클');
      for (const sx of [-1, 1]) {
        const box = bevelBox(0.026, 0.03, 0.006, 0.002);
        box.translate(sx * d.w * 0.26, d.h * 0.4, -d.d / 2 - 0.011);
        w.add(box, 'brass', 0.05);
        const tongue = bevelBox(0.008, 0.028, 0.005, 0.0015);
        tongue.translate(sx * d.w * 0.26, d.h * 0.46, -d.d / 2 - 0.014);
        w.add(tongue, 'brass', 0.05);
      }

      w.part('손잡이');
      // **온전한 토러스를 몸통에 반쯤 박는다.** 호로 자르면 두 끝이 열려 게이트에 걸린다
      const grip = new THREE.TorusGeometry(0.055, 0.0075, 7, 22);
      grip.translate(0, d.h, 0);
      w.add(grip, 'leather', 0.2);

      w.part('D링');
      for (const sx of [-1, 1]) {
        const r = new THREE.TorusGeometry(d.ring, 0.0028, 6, 16);
        r.rotateY(Math.PI / 2); // 옆면에 붙는다. 절반은 몸통 안이다
        r.translate(sx * (d.w / 2 - 0.004), d.h * 0.86, 0);
        w.add(r, 'brass', 0.05);
      }

      w.part('끊어진 어깨끈');
      // 오른쪽 D링에만 걸려 있다. 바닥까지 내려와 앞쪽으로 사려 있다
      const path = spline(
        [
          [d.w / 2 - 0.004, d.h * 0.86 - d.ring, 0],
          [d.w / 2 + 0.06, d.h * 0.5, -0.02],
          [d.w / 2 + 0.085, d.h * 0.12, -0.08],
          [d.w / 2 + 0.05, 0.005, -0.16],
          [d.w / 2 - 0.06, 0.005, -0.2],
          [d.w / 2 - 0.17, 0.005, -0.14],
        ],
        22,
      );
      ribbon(w, path, 0.026, 0.005, 'leather');

      w.part('풀린 올');
      // 뜯긴 끝. 가닥 넷이 갈라져야 「끊어졌다」가 형태로 읽힌다
      const tip = path[path.length - 1];
      for (let i = 0; i < 4; i++) {
        const a = -0.5 + i * 0.33;
        const g = bevelBox(0.0016, 0.0016, 0.026, 0.0004);
        g.rotateY(a);
        g.translate(tip.x - 0.012 + i * 0.006, 0.0035, tip.z - 0.012);
        w.add(g, 'leather', 0.05);
      }
    },
  },

  {
    id: 'jageop_belt',
    name: '작업벨트',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E25. 명주원 작업실 벽 공구걸이에 걸려 있다. 검은 합성섬유 띠 · 가죽 파우치 둘 · 사각 버클. 파우치 쪽이 무거워 한쪽만 길게 늘어진다 — 양쪽이 같은 길이로 처지면 걸린 물건이 아니라 그려 놓은 그림이다. */
    note: '벽 공구걸이에 걸린 작업벨트. 검은 합성섬유 띠에 가죽 파우치 둘과 사각 버클이 달렸다. 파우치 쪽이 무거워 한쪽만 길게 늘어지는 비대칭 처짐이 걸린 물건으로 읽히게 한다. dims 는 w(띠 폭)·span(걸린 폭)·drop(늘어진 길이)·pouch(파우치 크기).',
    hangs: true,
    sunk: true,
    dims: { w: 0.055, span: 0.16, drop: 0.46, pouch: 0.13 },
    /* 못에 걸린 벨트를 들어 떼어 낸다 — 못은 벽에 남는다 (2026-09-16) */
    openable: [{ part: '작업벨트', slide: [0, 0, -0.09] }],
    build(w, d) {
      w.part('못');
      const nail = lathe(
        [
          [0, 0],
          [0.0035, 0],
          [0.0035, 0.03],
          [0.006, 0.031],
          [0.006, 0.034],
          [0, 0.034],
        ],
        9,
      );
      nail.rotateX(Math.PI / 2); // 벽에서 앞(-Z)으로 나온다
      nail.translate(0, -0.004, -0.034);
      w.add(nail, 'steel', 0.05);

      const zc = -0.022; // 띠는 못에 걸려 벽에서 살짝 떠 있다

      // 못만 벽에 두고 벨트 전체를 여닫이로 — 들어 올려 떼어 낸다
      w.movable('작업벨트', () => {
      w.part('띠');
      // 왼쪽 — 버클 쪽. 가벼워서 짧게 걸린다
      ribbon(
        w,
        droop(
          new THREE.Vector3(-0.004, -0.006, zc),
          new THREE.Vector3(-d.span * 0.6, -d.drop * 0.62, zc - 0.012),
          0.03,
          6,
        ),
        d.w,
        0.006,
        'fabric',
      );
      // 오른쪽 — 파우치 쪽. 무게 때문에 길고 곧게 떨어진다
      const right = droop(
        new THREE.Vector3(0.004, -0.006, zc),
        new THREE.Vector3(d.span * 0.42, -d.drop, zc - 0.02),
        0.018,
        7,
      );
      ribbon(w, right, d.w, 0.006, 'fabric');

      w.part('버클');
      // 사각 링. TorusGeometry 의 tubularSegments 를 4 로 두면 네모난 고리가 된다
      const buckle = new THREE.TorusGeometry(0.026, 0.004, 4, 4);
      buckle.rotateZ(Math.PI / 4);
      buckle.scale(1, 0.78, 1);
      buckle.translate(-d.span * 0.6, -d.drop * 0.62 - 0.014, zc - 0.012);
      w.add(buckle, 'steel', 0.05);

      w.part('파우치');
      // 띠의 아래쪽 두 마디에 매달린다
      for (let i = 0; i < 2; i++) {
        const at = right[right.length - 2 - i * 2];
        const ph = d.pouch * (1 - i * 0.22);
        const shell = bevelBox(d.pouch * 0.78, ph, d.pouch * 0.44, 0.008, 2);
        shell.translate(at.x + (i === 0 ? 0.028 : -0.03), at.y - ph / 2 + 0.01, at.z - 0.014);
        w.add(shell, 'leather', 0.2);
        // 덮개 — 앞(-Z)으로 접혀 내린다
        const lid = bevelBox(d.pouch * 0.8, ph * 0.42, 0.006, 0.003);
        lid.translate(at.x + (i === 0 ? 0.028 : -0.03), at.y - ph * 0.22, at.z - 0.014 - d.pouch * 0.22 - 0.002);
        w.add(lid, 'leather', 0.2);
      }

      w.part('공구');
      // 드라이버 자루 하나가 파우치에서 삐져나와 있다. 이게 있어야 빈 주머니가 아니다
      const at = right[right.length - 2];
      const handle = lathe(
        [
          [0, 0],
          [0.008, 0.002],
          [0.009, 0.05],
          [0.0055, 0.056],
          [0, 0.056],
        ],
        10,
      );
      handle.rotateZ(0.22);
      // 자루 밑동은 파우치 **안**에 있어야 한다. 주머니 위에 띄우면 허공에 뜬 막대가 된다
      handle.translate(at.x + 0.05, at.y - d.pouch * 0.13, at.z - 0.014);
      w.add(handle, 'plastic', 0.1);
      });
    },
  },

  {
    id: 'camera_dslr',
    mount: 'surface',   // 책상·탁자 위에 놓인다. hangs 는 끈 때문(원점 규약)이지 벽걸이가 아니다
    nofront: true,
    name: '카메라와 스트랩',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E40. 회의실 송이경 좌석 등받이에 걸려 있다. 스트랩이 두 갈래로 내려와 러그에 물리고 바디는 렌즈를 아래로 늘어뜨린 채 돌아가 있다 — 걸어 둔 카메라는 절대 수평으로 안 매달린다. 스트랩 섬유가 현장 것과 대조된다. */
    note: '의자 등받이 등에 스트랩으로 걸어 둔 DSLR 카메라. 스트랩이 두 갈래로 내려와 바디를 매달고, 바디는 렌즈를 아래로 늘어뜨린 채 비스듬히 돌아가 있어 걸린 카메라로 읽힌다. dims 는 bodyW·bodyH·bodyD(바디)·lensR·lensL(렌즈)·drop(스트랩 길이)·span(갈래 폭).',
    hangs: true,
    sunk: true,
    dims: { bodyW: 0.135, bodyH: 0.096, bodyD: 0.07, lensR: 0.037, lensL: 0.098, drop: 0.28, span: 0.13 },
    /* 걸어 둔 카메라를 통째로 들어 올린다 — 스트랩째 (2026-09-16) */
    openable: [{ part: '카메라', slide: [0, 0, -0.07] }],
    build(w, d) {
      const top = -d.drop; // 바디 윗면

      w.movable('카메라', () => {
      w.part('스트랩');
      for (const sx of [-1, 1]) {
        ribbon(
          w,
          droop(
            new THREE.Vector3(sx * 0.012, -0.004, 0),
            new THREE.Vector3(sx * (d.bodyW / 2 - 0.006), top + 0.006, -0.004),
            0.012,
            6,
          ),
          0.019,
          0.004,
          'fabric',
        );
      }

      // 걸린 카메라는 살짝 돌아가 있다
      w.at(
        0,
        0,
        0,
        () => {
          w.part('바디');
          const body = bevelBox(d.bodyW, d.bodyH, d.bodyD, 0.008, 2);
          body.translate(0, top - d.bodyH / 2, 0);
          w.add(body, 'plastic', 0.18);

          w.part('그립');
          // 오른쪽(+X) 이 두툼하다. 이 비대칭이 카메라를 카메라로 만든다
          const grip = bevelBox(0.03, d.bodyH * 0.94, d.bodyD * 1.18, 0.01, 2);
          grip.translate(d.bodyW / 2 - 0.004, top - d.bodyH / 2, -0.004);
          w.add(grip, 'plastic', 0.18);

          w.part('펜타프리즘');
          const hump = bevelBox(0.042, 0.022, 0.05, 0.005);
          hump.translate(-0.008, top + 0.009, 0.004);
          w.add(hump, 'plastic', 0.18);

          w.part('마운트');
          const mount = lathe(
            [
              [0, 0],
              [d.lensR * 1.18, 0],
              [d.lensR * 1.18, 0.008],
              [0, 0.008],
            ],
            22,
          );
          mount.rotateX(-Math.PI / 2); // 축을 -Z 로 — 렌즈는 앞을 본다
          mount.translate(-0.008, top - d.bodyH * 0.52, -d.bodyD / 2 - 0.004);
          w.add(mount, 'steel', 0.06);

          w.part('렌즈');
          const barrel = lathe(
            [
              [0, 0],
              [d.lensR * 0.96, 0],
              [d.lensR * 0.96, d.lensL * 0.26],
              [d.lensR, d.lensL * 0.31],
              [d.lensR, d.lensL * 0.62],
              [d.lensR * 0.94, d.lensL * 0.66],
              [d.lensR * 0.94, d.lensL * 0.88],
              [d.lensR * 1.05, d.lensL * 0.91],
              [d.lensR * 1.05, d.lensL],
              [0, d.lensL],
            ],
            24,
          );
          barrel.rotateX(-Math.PI / 2);
          barrel.translate(-0.008, top - d.bodyH * 0.52, -d.bodyD / 2 - 0.01);
          w.add(barrel, 'plastic', 0.14);

          w.part('앞유리');
          const front = lathe(
            [
              [0, 0],
              [d.lensR * 0.86, 0],
              [d.lensR * 0.86, 0.0025],
              [0, 0.0025],
            ],
            22,
          );
          front.rotateX(-Math.PI / 2);
          front.translate(-0.008, top - d.bodyH * 0.52, -d.bodyD / 2 - 0.01 - d.lensL + 0.001);
          w.add(front, 'glass', 0);

          w.part('셔터');
          const sh = lathe(
            [
              [0, 0],
              [0.006, 0],
              [0.006, 0.003],
              [0, 0.003],
            ],
            10,
          );
          sh.translate(d.bodyW / 2 - 0.012, top + 0.001, -0.012);
          w.add(sh, 'steel', 0.04);

          w.part('다이얼');
          const dial = lathe(
            [
              [0, 0],
              [0.014, 0],
              [0.014, 0.009],
              [0.012, 0.011],
              [0, 0.011],
            ],
            14,
          );
          dial.translate(-d.bodyW / 2 + 0.02, top - 0.002, 0.006);
          w.add(dial, 'steel', 0.04);
        },
        0.14,
        0,
        -0.1,
      );
      });
    },
  },

  /* ---------------- 결합 기믹 ---------------- */
  {
    id: 'mug_shard',
    nofront: true,
    name: '깨진 흰 머그 조각',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E28 · 기믹 G06(파편 맞추기). 한서진 집 거실-침실 사이 바닥. 밑동은 형태가 남았고 벽 조각 일곱이 흩어졌으며 손잡이는 두 동강 났다. 밑동 옆에 마른 피가 고여 있다 — 가마실 파편통(E29)의 큰 조각과 맞물린다. */
    note: '바닥에 흩어진 흰 머그 파편. 밑동은 형태가 남아 있고 벽 조각 일곱이 주위에 흩어졌으며 손잡이는 두 동강 나 있다. 밑동 옆에 마른 액체 자국이 고여 있다. dims 는 spread(흩어진 반경)·base(밑동 반지름)·shard(조각 크기).',
    clusters: 14, // 깨진 물건이다. 떨어져 있는 것이 정상이고, 세는 것이 선언이다
    dims: { spread: 0.33, base: 0.036, shard: 0.045 },
    /* 형태가 남은 밑동을 집어 맞춰 본다 — G06 맞물림의 한쪽 (2026-09-16) */
    openable: [{ part: '밑동', slide: [0, 0, -0.04] }],
    build(w, d) {
      w.part('밑동');
      // 굽까지 남은 아래쪽. 이게 있어야 「머그였던 것」으로 읽힌다
      w.movable('밑동', () => {
        const foot = lathe(
          [
            [0, 0],
            [d.base, 0],
            [d.base, 0.004],
            [d.base * 0.94, 0.006],
            [d.base * 0.94, 0.026],
            [d.base, 0.03],
            [d.base * 0.86, 0.03],
            [d.base * 0.86, 0.008],
            [0, 0.008],
          ],
          22,
        );
        foot.rotateZ(0.22); // 굴러 기울었다
        foot.translate(-d.spread * 0.16, 0.006, 0.02);
        w.add(foot, 'porcelain', 0.06);
      });

      w.part('벽 조각');
      // 컵 벽이었던 조각들. 컵 반지름(36mm)만큼 휘어 있어야 「깨진 그릇」으로 읽힌다.
      // 평평한 판으로 두면 흩어진 트럼프 카드가 된다 — 처음에 그렇게 나왔다
      for (let i = 0; i < 7; i++) {
        const g = shard(i * 5 + 1, d.shard * (0.5 + rnd(i) * 0.75), 0.0035, 1 / (2 * d.base));
        g.rotateX((rnd(i + 40) - 0.5) * 1.1); // 엎어진 것과 뒤집힌 것이 섞인다
        g.rotateY(rnd(i + 60) * Math.PI);
        g.rotateZ((rnd(i + 80) - 0.5) * 0.6);
        const a = (i / 7) * Math.PI * 2 + rnd(i + 100);
        const rad = d.spread * (0.12 + rnd(i + 120) * 0.38);
        g.translate(Math.cos(a) * rad, 0.007 + rnd(i + 140) * 0.006, Math.sin(a) * rad);
        w.add(g, 'porcelain', 0.05);
      }

      w.part('손잡이 조각');
      // 두 동강. 머그 손잡이는 **납작한 띠**다 — 단면을 정사각형으로 두면
      // 모깎기가 다 먹어 강낭콩이 된다. 실제로 그렇게 나왔다
      for (const [ox, oz, rot] of [
        [d.spread * 0.3, -d.spread * 0.14, 0.4],
        [d.spread * 0.4, -d.spread * 0.3, -1.1],
      ] as const) {
        for (let k = 0; k < 3; k++) {
          const seg = bevelBox(0.018, 0.0055, 0.012, 0.0012);
          seg.rotateY(-0.45 + k * 0.45);
          seg.rotateZ(rot * 0.2); // 두 동강이 서로 다른 각도로 엎어져 있다
          seg.translate(ox + k * 0.015, 0.004, oz + Math.abs(k - 1) * 0.006);
          w.add(seg, 'porcelain', 0.04);
        }
      }

      w.part('핏자국');
      // 마른 피. 밑동 옆에만 고였다 — 사방에 뿌리면 현장이 아니라 무대가 된다
      w.thinSurface(() => {
        // 단면 원판(CircleGeometry)을 깔면 안 된다. blood 재질은 FrontSide 라
        // 아래에서 본 앞면 실루엣에 안 잡혀 catalog-check 화면 투과가 FAIL 난다.
        // 그리고 무엇보다 물감 자국으로 보인다 — bloodPool 은 가장자리에
        // 메니스커스를 준 닫힌 솔리드다
        for (let i = 0; i < 5; i++) {
          const c = bloodPool(0.012 + rnd(i + 7) * 0.019, i * 31 + 5, 0.0035);
          c.scale(1, 1, 0.6 + rnd(i + 17) * 0.7);
          c.translate(-d.spread * 0.2 + (rnd(i) - 0.5) * 0.06, 0.0004, 0.03 + (rnd(i + 3) - 0.5) * 0.05);
          w.add(c, 'blood', 0);
        }
      });
    },
  },

  {
    id: 'pagyeon_tong',
    nofront: true,
    name: '소성불량 파편통',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E29 · 기믹 G06. 가마실. 구워서 실패한 것을 깨 담는 통이라 아무도 안 뒤진다 — 그래서 여기 숨겼다. 통 둘레에 붉은 표시 띠가 감겨 있고 파편이 바닥부터 차 있다.
       **위층 파편은 `pagyeon_layer`(헤집는 덮개), 그 밑의 흰 머그 조각은 `mug_shard_big` 이다** — 셋을 겹쳐 놓는다. 이 조각만 놓으면 통이 반쯤 빈 것으로 보인다. */
    note: '소성불량 도자기를 깨 담는 큰 원통 통. 통 둘레에 붉은 표시 띠가 감겨 있고 안에는 파편이 바닥부터 차 있다. 위층 덮개(`pagyeon_layer`)와 그 밑 조각(`mug_shard_big`)을 겹쳐 놓아야 통이 가득 찬 것으로 보인다. dims 는 r·h·wall(벽 두께).',
    clusters: 3,
    dims: { r: 0.21, h: 0.4, wall: 0.008 },
    /* 림에 걸터앉은 파편 하나를 집어 든다 (2026-09-16) */
    openable: [{ part: '림 위 파편', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.part('통');
      // 바깥벽 → 림 → 안쪽벽 → 안바닥. 한 프로파일로 돌면 위상이 닫힌다
      const drum = lathe(
        [
          [0, 0],
          [d.r * 0.84, 0],
          [d.r * 0.9, 0.02],
          [d.r, d.h],
          [d.r - d.wall, d.h],
          [d.r * 0.9 - d.wall, 0.02],
          [d.r * 0.84 - d.wall, 0.014],
          [0, 0.014],
        ],
        28,
      );
      w.add(drum, 'steel', 0.35);

      w.part('표시 띠');
      // 프로파일 첫 점과 끝 점이 같으면 캡 없이 닫힌다 — 통 벽에 감긴 테이프가 된다
      const y0 = d.h * 0.62;
      const y1 = d.h * 0.72;
      const rr = d.r * 0.965;
      const band = lathe(
        [
          [rr, y0],
          [rr + 0.004, y0],
          [rr + 0.004, y1],
          [rr, y1],
          [rr, y0],
        ],
        28,
      );
      w.add(band, 'paint', 0.25);

      w.part('손잡이');
      // 온전한 토러스를 반쯤 묻는 수법은 **속이 찬 몸통**에서만 통한다.
      // 이 통은 비어 있어서 위에서 내려다보면 아래 절반이 그대로 보였다.
      // 그래서 위쪽 반원만 띠로 만들고, 양 끝을 통 벽 두께 속에 박는다
      const R = d.r * 0.968; // 벽 두께 한가운데
      const yAtt = d.h - 0.03;
      const arc: THREE.Vector3[] = [];
      for (let i = 0; i <= 16; i++) {
        const a = (Math.PI * i) / 16;
        arc.push(new THREE.Vector3(-Math.cos(a) * R, yAtt + Math.sin(a) * R * 0.62, 0));
      }
      ribbon(w, arc, 0.0055, 0.0055, 'steel');

      w.part('파편 더미');
      // 「입구까지 차 있다」를 위쪽 조각 몇 개로만 표현하면 파편이 허공에 뜬 층이 된다.
      // 바닥부터 입구 아래까지 채운 더미를 두고 그 위에 조각을 얹는다
      /* **흰색에 입구까지 찬 더미는 우유였다**(게임 컷 2026-09-09). 위층(`pagyeon_layer`)을 열면
       * 이 더미가 보이는데, 소성불량 파편은 흙색·회갈색이고 통의 **절반쯤**만 찬다 — 그래야
       * 그 위에 얹힌 흰 머그 조각(`mug_shard_big`, y≈0.24)이 튄다 */
      const heap = lathe(
        [
          [0, 0.014],
          [0.176, 0.02],
          [0.196, d.h * 0.52],
          [0.15, d.h * 0.6],
          [0, d.h * 0.58],
        ],
        26,
      );
      paintGeometry(heap, '#7a6a58');   // 소성불량 — 흙색
      w.add(heap, 'porcelain', 0.09);

      /* **림에 걸터앉은 파편 하나** — 곁가지. 더미를 넣은 뒤 던져 올린 마지막 조각이
       * 아직 림에 걸려 있다. E 키로 집어 든다 (2026-09-16) */
      w.movable('림 위 파편', () => {
        const g = shard(97, 0.05, 0.004, 7);
        g.rotateX(0.5);
        g.rotateY(0.8);
        g.translate(d.r * 0.55, d.h - 0.012, -d.r * 0.62);
        paintGeometry(g, '#8a7b66');
        w.add(g, 'porcelain', 0.05);
      });

      /* **위층 파편은 이 조각에서 뺐다** (2026-09-09, 파이프라인 ②).
       * E29 는 「깊숙한 곳」을 심층수색하는 단서다 — 헤집을 수 있으려면 위층이 **움직이는 부품**이어야
       * 하고, 몸통과 한 덩어리면 프롭이 그 부분만 못 옮긴다. 위층은 `pagyeon_layer`,
       * 그 밑에 숨은 흰 조각은 `mug_shard_big` 이다. 이 조각은 통 + 바닥 더미까지다. */
    },
    materials: () => ({
      paint: new THREE.MeshStandardMaterial({
        color: new THREE.Color('#9c3a2c'), // 사탕색이 되지 않게 채도를 내린다
        roughness: 0.7,
        metalness: 0,
        vertexColors: true,
      }),
    }),
  },

  /* ---------------------------------------------------------------- *
   * 파편통 위층 — **헤집는 덮개**(prop kind `lid`). 파이프라인 ② 2026-09-09
   * ---------------------------------------------------------------- */
  {
    id: 'pagyeon_layer',
    name: '파편통 위층 파편',
    collection: 'sahol',
    category: '단서',
    note:
      'E29 의 덮개. `pagyeon_tong` 입구를 덮은 **위층 파편 한 겹**이다. 심층수색하면 이 겹이 옆으로 밀리고 ' +
      '밑의 흰 머그 조각(`mug_shard_big`)이 드러난다.\n' +
      '**한 겹으로 읽혀야 한다** — 조각을 흩뿌리면 통 위에 떠 있는 낱개가 된다. 얕은 접시 모양 바닥에 ' +
      '조각을 박아 서로 물리게 한다. 밑면이 통 입구(안지름 0.196)를 덮어야 헤집기 전에 안이 안 보인다.\n' +
      '**색이 흰색이면 안 된다.** 소성불량은 덜 구워진 흙색·과소성으로 그을린 회갈색이다 — 흰 머그 조각이 ' +
      '이 사이에서 튀어야 「하나가 다르다」가 읽힌다. 원점은 겹의 **밑면 가운데**.',
    dims: { r: 0.19, h: 0.055 },
    clusters: 1,
    /* 헤집는 덮개 — 겹 통째로 옆으로 밀리며 밑의 흰 조각이 드러난다 (2026-09-16) */
    openable: [{ part: '파편 겹', slide: [0.15, 0, 0.02] }],
    build(w, d) {
      w.movable('파편 겹', () => {
      w.part('겹 바닥');
      // 얕은 접시. 이것이 없으면 조각들이 허공에 뜬 낱개로 보이고 통 안도 들여다보인다
      const dish = lathe(
        [
          [0, 0.004],
          [d.r * 0.86, 0.002],
          [d.r, 0.012],
          [d.r, 0.02],
          [d.r * 0.86, 0.012],
          [0, 0.014],
        ],
        26,
      );
      // **접시가 보이면 안 된다.** 밝은 회색으로 뒀더니 위에서 「쿠키 위 초콜릿」으로 읽혔다 —
      // 조각 사이로 드러나는 면이라 통 안처럼 어두워야 한다
      paintGeometry(dish, '#3f3a34');
      w.add(dish, 'porcelain', 0.08);

      w.part('파편');
      /* **기울기가 크기를 이기면 안 된다** (joint-check 2026-09-09).
       * ±1.5rad 로 흔들었더니 반지름 29mm 짜리 조각이 세로로 ±29mm 를 먹어 바닥을 9mm 뚫고
       * 접시에서 10~16mm 떠 6덩이가 됐다. 세로 여유 = 반지름 × sin(기울기) 다 —
       * 기울기를 ±0.45rad 로 묶으면 최대 12mm, 접시 윗면(y 0.012~0.02) 위에 얹혀 한 덩이가 된다. */
      // **겹은 빽빽해야 한다.** 22개를 흩었더니 접시가 그대로 보여 「담긴 층」이 아니라
      // 「접시에 얹은 몇 조각」이었다. 수를 늘리고 키워 서로 겹치게 한다
      for (let i = 0; i < 40; i++) {
        const a = rnd(i * 2.7) * Math.PI * 2;
        const rad = d.r * 0.92 * Math.sqrt(rnd(i * 5.1));
        const half = 0.014 + rnd(i * 7.3) * 0.020;          // 조각 반지름 14~34mm
        const g = shard(i * 3 + 41, half * 2, 0.004, 5 + rnd(i * 11.7) * 8);
        g.rotateX((rnd(i * 13.1) - 0.5) * 0.7);             // ±0.35rad
        g.rotateY(rnd(i * 17.3) * Math.PI);
        g.rotateZ((rnd(i * 19.7) - 0.5) * 0.7);
        /* **접시 윗면(y≈0.013)에 닿게 얹는다.** `half*0.45` 를 무조건 더했더니 기울기가 작은 조각은
         * 밑면이 그 높이 그대로라 10mm 떴다(joint-check). 조각의 로컬 밑면은 y=0 이므로
         * 올림값은 기울어져 내려가는 깊이(half·sin(기울기)≈half·0.3)만큼이면 충분하다. */
        g.translate(Math.cos(a) * rad, 0.014 + half * 0.30, Math.sin(a) * rad);
        // 소성불량 — 덜 구워진 흙색과 과소성으로 그을린 것이 섞인다. 흰색은 쓰지 않는다
        const t = rnd(i * 29.3);
        paintGeometry(g, t < 0.34 ? '#8a7b66' : t < 0.68 ? '#6d6258' : '#4a443e');
        w.add(g, 'porcelain', 0.05);
      }
      });
    },
  },

  /* ---------------------------------------------------------------- *
   * 파편통 밑의 흰 머그 조각 — E29 의 단서 본체
   * ---------------------------------------------------------------- */
  {
    id: 'mug_shard_big',
    name: '큰 흰 머그 조각',
    collection: 'sahol',
    category: '단서',
    note:
      'E29 · 기믹 G06. 파편통 위층(`pagyeon_layer`)을 헤집으면 나오는 조각. **거실의 `mug_shard`(E28)와 ' +
      '같은 머그다** — 반지름을 같은 밑동값(0.036)으로 맞춰야 나중에 맞춰 볼 수 있다.\n' +
      '**형태 정의(2026-09-09, Chris 판정 뒤 다시 씀)**: 두꺼운 원통 벽의 한 조각. 깨진 선은 **크게 서너 번** ' +
      '꺾인다 — 잘게 톱니면 종이 찢은 것이다. 손잡이 뿌리는 벽에서 **자라 나온 둥근 혹**의 부러진 그루터기, ' +
      '얹어 놓은 상자가 아니다. 놓임은 **한쪽으로 기울어** 한 끝과 배가 닿는다 — 두 끝으로 선 터널이 아니다.\n' +
      '흰 유약 바깥면 · 부러진 단면만 무광 흙색. 원점은 놓인 자리 바닥 가운데.',
    dims: { r: 0.036, wall: 0.006, h: 0.05, arc: 2.1 },
    clusters: 1,
    /* 위층을 헤집으면 나오는 본체 — 집어 거실 밑동과 맞춰 본다 (2026-09-16) */
    openable: [{ part: '흰 조각', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.movable('흰 조각', () => {
      /* ── 벽 조각: 고리 부채꼴 → 높이로 밀어내기 ── */
      const sh = new THREE.Shape();
      sh.absarc(0, 0, d.r, -d.arc / 2, d.arc / 2, false);
      sh.absarc(0, 0, d.r - d.wall, d.arc / 2, -d.arc / 2, true);
      const g = new THREE.ExtrudeGeometry(sh, { depth: d.h, bevelEnabled: false, curveSegments: 16 });
      g.rotateX(-Math.PI / 2);        // +Z 밀어낸 축 → +Y

      /* ── 깨진 선: 큰 꺾임 서너 번 ──
       * 각도(θ)에 따라 위·아래 높이를 정한다. 꺾임점 4개를 잡고 그 사이는 직선으로 잇는다.
       * 잘게 흔든 톱니(전 판)는 종이였다 — 도자기는 몇 번 크게 나간다 */
      const kn = [-d.arc / 2, -d.arc * 0.18, d.arc * 0.12, d.arc * 0.38, d.arc / 2];
      const topAt = [d.h * 0.62, d.h * 0.95, d.h * 0.78, d.h, d.h * 0.7];       // 위 깨진 선
      const botAt = [d.h * 0.22, d.h * 0.04, d.h * 0.16, d.h * 0.0, d.h * 0.12];  // 아래 깨진 선
      const lerpAt = (th: number, arr: number[]) => {
        for (let k = 0; k < kn.length - 1; k++) {
          if (th <= kn[k + 1]) {
            const t = (th - kn[k]) / (kn[k + 1] - kn[k]);
            return arr[k] + (arr[k + 1] - arr[k]) * t;
          }
        }
        return arr[arr.length - 1];
      };
      const pos = g.attributes.position;
      for (let k = 0; k < pos.count; k++) {
        const x = pos.getX(k), y = pos.getY(k), z = pos.getZ(k);
        const th = Math.atan2(-z, x);                       // 밀어낸 뒤 호는 (r·cosθ, −r·sinθ)
        const t = y / d.h;                                  // 0=아래 캡, 1=위 캡
        pos.setY(k, botAt[0] * 0 + lerpAt(th, botAt) + (lerpAt(th, topAt) - lerpAt(th, botAt)) * t);
      }
      pos.needsUpdate = true;
      g.computeVertexNormals();

      /* ── 손잡이 뿌리: 벽에서 자라 나온 혹의 그루터기 ──
       * 반구를 반지름 방향으로 눌러 벽 바깥면에 **묻고**, 바깥으로 6mm 만 나오게 한다.
       * 부러진 면이 보이게 바깥쪽을 평평하게 자른다(반구 → 그루터기) */
      const stumps: THREE.BufferGeometry[] = [];
      /* ── 손잡이 토막 (2026-09-09, Chris 「손잡이가 너무 부자연스러움」) ──
       * 원기둥 마개를 세운 게 틀렸다. 머그 손잡이는 **납작한 띠**가 벽에서 나와 **곧장 휘기 시작**하고,
       * 부러지면 그 휘기 시작한 짧은 토막이 남는다 — 위 토막은 아래로, 아래 토막은 위로(C 의 시작).
       * 길이는 서로 다르게(같은 자리에서 안 부러진다).
       * 타원 단면(두께 8·폭 13mm)을 `extrudePath` 로 짧은 호를 따라 밀어낸다 — 양 끝 캡이 닫혀
       * 몸체가 닫히고, 먼 끝 캡이 부러진 단면이다(캡 그룹 = materialIndex 0 → 흙색). */
      const oval = new THREE.Shape();
      oval.absellipse(0, 0, 0.004, 0.0065, 0, Math.PI * 2, false, 0);
      const x0 = d.r - 0.002;                            // 벽 바깥면 안쪽 2mm 에서 시작(묻힘)
      // 17·12mm 는 뿔 둘이었다(세 각도 컷). 부러진 뿌리는 5~10mm 만 남는다
      for (const [vy, dy, len] of [[0.72, -1, 0.009], [0.27, 1, 0.006]] as const) {
        const y0 = vy * d.h;
        const path = new THREE.QuadraticBezierCurve3(
          new THREE.Vector3(x0, y0, 0),
          new THREE.Vector3(x0 + len * 0.7, y0 + dy * len * 0.05, 0),
          new THREE.Vector3(x0 + len * 1.0, y0 + dy * len * 0.35, 0),
        );
        const st = new THREE.ExtrudeGeometry(oval, { extrudePath: path, steps: 6, bevelEnabled: false, curveSegments: 10 });
        paintGeometry(st, '#f2f0ea');
        {
          const col = st.attributes.color as THREE.BufferAttribute;
          const raw = new THREE.Color('#a89b86');
          for (const grp of st.groups) {
            if (grp.materialIndex !== 0) continue;
            for (let k = grp.start; k < grp.start + grp.count; k++) col.setXYZ(k, raw.r, raw.g, raw.b);
          }
        }
        stumps.push(st);
      }

      /* ── 놓임: 한쪽으로 기울어 한 끝과 배가 닿는다 ── */
      const lay = (geo: THREE.BufferGeometry) => {
        geo.rotateZ(Math.PI / 2 + 0.32);   // 눕히되 수평이 아니게
        geo.rotateX(0.22);                 // 한쪽 끝이 들린다
        geo.rotateY(0.7);
      };
      lay(g);
      for (const st of stumps) lay(st);
      g.computeBoundingBox();
      const lift = -g.boundingBox!.min.y; // 올리기 **전에** 잡는다 — translate 가 bbox 를 다시 계산한다
      g.translate(0, lift, 0);
      for (const st of stumps) st.translate(0, lift, 0);

      w.part('벽 조각');
      paintGeometry(g, '#f6f5f1');
      {
        // 캡 그룹(materialIndex 0)만 무광 흙색 — 부러진 단면
        const col = g.attributes.color as THREE.BufferAttribute;
        const raw = new THREE.Color('#a89b86');
        for (const grp of g.groups) {
          if (grp.materialIndex !== 0) continue;
          for (let k = grp.start; k < grp.start + grp.count; k++) col.setXYZ(k, raw.r, raw.g, raw.b);
        }
        col.needsUpdate = true;
      }
      w.add(g, 'porcelain', 0.06);

      w.part('손잡이 뿌리');
      for (const st of stumps) w.add(st, 'porcelain', 0.05);   // 색은 위에서 이미 구웠다
      });
    },
  },

  {
    id: 'torn_paper',
    name: '찢어진 감압용지 조각',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E11 · 기믹 G08(계약서 맞추기). 붙박이장 안, 시신 오른손 아래에서 나왔다. 찢긴 변이 톱니라 재무실 잠금서류함의 원본철(E15·E16)과 물리적으로 맞물린다. 필기가 아니라 **눌린 자국**이다 — 위에서 쓴 것이 베껴진 뒷장이다. */
    note: '찢어진 감압용지 조각. 얇은 종이 한 장의 일부로, 찢긴 변이 톱니처럼 불규칙하다. 표면에는 잉크 필기가 아니라 위에서 눌러 쓴 자국만 남아 있다. dims 는 w·h·t(종이 두께).',
    dims: { w: 0.088, h: 0.125, t: 0.0007 },
    /* 찢긴 조각을 집어 원본철과 맞춰 본다 — G08 의 한쪽 (2026-09-16) */
    openable: [{ part: '종이 조각', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.movable('종이 조각', () => {
      // 오른쪽 변이 찢긴 조각. Shape 를 뽑아 두께를 주면 닫힌 몸체라 종이면서도 solid 다
      const sh = new THREE.Shape();
      sh.moveTo(-d.w / 2, -d.h / 2);
      sh.lineTo(-d.w / 2, d.h / 2);
      sh.lineTo(d.w * 0.28, d.h / 2);
      // 톱니 — 찢은 자국. 직선으로 자르면 가위질이 되고 단서가 죽는다
      const teeth = 9;
      let maxX = d.w * 0.28;
      for (let i = 0; i < teeth; i++) {
        const t = i / (teeth - 1);
        const y = d.h / 2 - t * d.h;
        const x = d.w * (0.28 + (rnd(i * 3) - 0.35) * 0.5);
        maxX = Math.max(maxX, x);
        sh.lineTo(x, y);
      }
      sh.lineTo(-d.w / 2, -d.h / 2);
      // 두 조각을 **같은 좌표계**로 매핑한다 — 접힌 귀는 이 종이에서 말려 올라온 부분이다
      const uvW = maxX + d.w / 2;

      w.part('조각');
      const geo = new THREE.ExtrudeGeometry(sh, { depth: d.t, bevelEnabled: false });
      sheetUV(geo, -d.w / 2, -d.h / 2, uvW, d.h);
      geo.rotateX(-Math.PI / 2); // Shape 는 XY 평면에 있다. 바닥에 눕힌다
      geo.rotateY(0.3);
      geo.translate(0, d.t / 2, 0);
      w.add(geo, 'paper', 0);

      w.part('접힌 귀');
      // 왼쪽 아래 모서리가 말려 올라와 있다. 완전히 평평하면 프린트한 도형으로 보인다
      const corner = new THREE.Shape();
      corner.moveTo(0, 0);
      corner.lineTo(0.026, 0);
      corner.lineTo(0, 0.03);
      const cg = new THREE.ExtrudeGeometry(corner, { depth: d.t, bevelEnabled: false });
      sheetUV(cg, -d.w / 2, -d.h / 2, uvW, d.h);
      cg.rotateX(-Math.PI / 2 + 0.55);
      cg.rotateY(0.3);
      cg.translate(-d.w * 0.36, d.t * 1.2, d.h * 0.3);
      w.add(cg, 'paper', 0);
      });
    },
    materials: () => ({
      paper: printedFace(
        (g, W, H) => {
          // 감압용지 뒷장. 잉크가 아니라 눌려서 번진 남색 자국이다
          g.fillStyle = 'rgba(64,80,116,0.66)'; // 눌린 자국이지만 「글이 있는 종이」로는 읽혀야 한다
          for (let r = 0; r < 7; r++) {
            const y = H * (0.16 + r * 0.1);
            let x = W * 0.12;
            const n = 2 + Math.floor(rnd(r * 11) * 3);
            for (let k = 0; k < n; k++) {
              const len = W * (0.1 + rnd(r * 5 + k) * 0.24);
              g.fillRect(x, y, len, 5);
              x += len + W * 0.05;
            }
          }
          // 서명 자리 — 한 줄만 진하다
          g.fillStyle = 'rgba(46,60,92,0.88)';
          g.fillRect(W * 0.12, H * 0.86, W * 0.42, 7);
          // 구겨진 결
          g.strokeStyle = 'rgba(150,140,120,0.22)';
          g.lineWidth = 2;
          for (let i = 0; i < 6; i++) {
            g.beginPath();
            g.moveTo(rnd(i) * W, 0);
            g.lineTo(rnd(i + 9) * W, H);
            g.stroke();
          }
        },
        320,
        440,
        '#efe4d6', // 감압용지는 흰색이 아니다. 누런 분홍빛이 돈다
      ),
    }),
  },
  BODY_PIECE,
];
